/*
@license
  Be Yours by roartheme (https://roartheme.co)
  Access unminified JS in assets/global.js

  Use this event listener to run your own JS outside of this file.
  Documentation - https://roartheme.co/blogs/beyours/javascript-events-for-developers

  document.addEventListener('page:loaded', function() {
    // Page has loaded and theme assets are ready
  });
*/
window.theme = window.theme || {};

theme.config = {
  mqlSmall: false,
  mediaQuerySmall: 'screen and (max-width: 749px)',
  isTouch: ('ontouchstart' in window) || window.DocumentTouch && window.document instanceof DocumentTouch || window.navigator.maxTouchPoints || window.navigator.msMaxTouchPoints ? true : false,
  rtl: document.documentElement.getAttribute('dir') === 'rtl' ? true : false
};

if (console && console.log) {
  console.log(theme.settings.themeName + ' theme (' + theme.settings.themeVersion + ') by RoarTheme | Learn more at https://roartheme.co');
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

theme.Currency = (function() {
  const moneyFormat = '${{amount}}'; // eslint-disable-line camelcase

  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || moneyFormat;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        '$1' + thousands
      );
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  function getBaseUnit(variant) {
    if (!variant) {
      return;
    }

    if (!variant.unit_price_measurement || !variant.unit_price_measurement.reference_value) {
      return;
    }

    return variant.unit_price_measurement.reference_value === 1
      ? variant.unit_price_measurement.reference_unit
      : variant.unit_price_measurement.reference_value +
          variant.unit_price_measurement.reference_unit;
  }

  return {
    formatMoney: formatMoney,
    getBaseUnit: getBaseUnit
  };
})();

// Init section function when it's visible, then disable observer
theme.initWhenVisible = function(options) {
  const threshold = options.threshold ? options.threshold : 0;

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (typeof options.callback === 'function') {
          options.callback();
          observer.unobserve(entry.target);
        }
      }
    });
  }, {rootMargin: `0px 0px ${threshold}px 0px`});

  observer.observe(options.element);
};

(function() {
  // Trigger events when going between breakpoints
  const mql = window.matchMedia(theme.config.mediaQuerySmall);
  theme.config.mqlSmall = mql.matches;
  mql.onchange = (e) => {
    if (e.matches) {
      theme.config.mqlSmall = true;
      document.dispatchEvent(new CustomEvent('matchSmall'));
    }
    else {
      theme.config.mqlSmall = false;
      document.dispatchEvent(new CustomEvent('unmatchSmall'));
    }
  }

  // Page transition
  document.addEventListener('click', (event) => {
    const anchor = event.target.closest('a');
    if (anchor?.href && /^(mailto:|tel:)/.test(anchor.href)) {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    } else {
      window.addEventListener('beforeunload', beforeUnloadHandler);
    }
  });
  const beforeUnloadHandler = (event) => {
    document.body.classList.add('unloading');
  }
  window.addEventListener('DOMContentLoaded', () => {
    document.body.classList.add('loaded');

    document.dispatchEvent(new CustomEvent('page:loaded'));
  });
  window.addEventListener('pageshow', (event) => {
    // Removes unload class when returning to page via history
    if (event.persisted) {
      document.body.classList.remove('unloading');
    }
  });

  // Viewport height
  function viewportHeight() {
    if (window.matchMedia('(max-width: 990px)')) {
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    }

    setHeaderBottomPosition();
  }
  window.addEventListener('resize', viewportHeight);
  window.addEventListener('DOMContentLoaded', viewportHeight);

  if (document.body.getAttribute('data-page-rendering') !== null) {
    // Improve initial load time by skipping the rendering of offscreen content
    const observer = new IntersectionObserver(
      (entries, _observer) => {
        entries.forEach((entry) => {
          const el = entry.target;
          // Not currently in intersection area.
          if (entry.intersectionRatio == 0) {
            return;
          }
          // Trigger rendering for elements within scroll area that haven't already been marked.
          if (!el.markedVisible) {
            el.attributeStyleMap && el.attributeStyleMap.set('content-visibility', 'visible');
            el.markedVisible = true;
          }
        });
      },
      // Set a rendering 50px above and 100px below the main scroll area.
      { rootMargin: '50px 0px 100px 0px' }
    );
    const sections = document.querySelectorAll('.shopify-section + .shopify-section');
    sections.forEach((section) => { observer.observe(section); });
  }

  /*
  // Delay JavaScript until user interaction
  const loadScriptsTimer = setTimeout(loadScripts, 5000);
  const userInteractionEvents = ["mouseover", "mousemove", "keydown", "touchstart", "touchend", "touchmove", "wheel"];
  userInteractionEvents.forEach(function(event) {
    window.addEventListener(event, triggerScriptLoader, {
      passive: !0
    });
  });
  function triggerScriptLoader() {
    loadScripts();
    clearTimeout(loadScriptsTimer);
    userInteractionEvents.forEach(function(event) {
      window.removeEventListener(event, triggerScriptLoader, {
        passive: !0
      });
    });
  }
  function loadScripts() {
    document.querySelectorAll("script[data-type='lazy']").forEach(function(script) {
      script.setAttribute("src", script.getAttribute("data-src"));
    });
  }
  */
})();

// Prevent vertical scroll while using flickity sliders
(function() {
  var e = !1;
  var t;

  document.body.addEventListener('touchstart', function(i) {
    if (!i.target.closest('.flickity-slider')) {
      return e = !1;
    }
    e = !0;
    t = {
      x: i.touches[0].pageX,
      y: i.touches[0].pageY
    }
  })

  document.body.addEventListener('touchmove', function(i) {
    if (e && i.cancelable) {
      var n = {
        x: i.touches[0].pageX - t.x,
        y: i.touches[0].pageY - t.y
      };
      Math.abs(n.x) > Flickity.defaults.dragThreshold && i.preventDefault()
    }
  }, { passive: !1 })
})();

function isStorageSupported (type) {
  // Return false if we are in an iframe without access to sessionStorage
  if (window.self !== window.top) {
    return false;
  }

  const testKey = 'beyours:test';
  let storage;
  if (type === 'session') {
    storage = window.sessionStorage;
  }
  if (type === 'local') {
    storage = window.localStorage;
  }

  try {
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
    return true;
  }
  catch (error) {
    // Do nothing, this may happen in Safari in incognito mode
    return false;
  }
}

function checkScrollbar() {
  const rect = document.body.getBoundingClientRect();
  return Math.round(rect.left + rect.right) < window.innerWidth;
}

function setScrollbarWidth() {
  if (checkScrollbar()) {
    const element = document.createElement('span');
    element.className = 'modal-scrollbar-measure', document.body.appendChild(element);
  
    const width = element.getBoundingClientRect().width - element.clientWidth;
    document.body.removeChild(element);
    document.documentElement.style.setProperty('--scrollbar-width', `${width}px`);
  }
}

function setHeaderBottomPosition() {
  const header = document.querySelector('.shopify-section-header');
  if (header) {
    document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(header.getBoundingClientRect().bottom)}px`);
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
    if (charCode !== 'TAB') return; // If not TAB key
    
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (elementToFocus) elementToFocus.focus();

  if (elementToFocus?.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(elementToFocus.type) && elementToFocus.value) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = ['ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'TAB', 'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN']
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if(navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

    if (mouseClick) return;

    currentFocusedElement = document.activeElement;
    currentFocusedElement.classList.add('focused');

  }, true);
}

function pauseAllMedia() {
  document.querySelectorAll('iframe.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('iframe.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video-section[data-type="youtube"]:not([data-video-hero]) iframe').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('video-section[data-type="vimeo"]:not([data-video-hero]) iframe').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => {
    if (!video.closest('[data-video-hero]')) video.pause();
  });
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
  if (charCode !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

function filterShopifyEvent(event, domElement, callback) {
  let executeCallback = false;
  if (event.type.includes('shopify:section')) {
    if (domElement.hasAttribute('data-section-id') && domElement.getAttribute('data-section-id') === event.detail.sectionId) {
      executeCallback = true;
    }
  }
  else if (event.type.includes('shopify:block') && event.target === domElement) {
    executeCallback = true;
  }
  if (executeCallback) {
    callback(event);
  }
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

const serializeForm = form => {
  const obj = {};
  const formData = new FormData(form);

  for (const key of formData.keys()) {
    const regex = /(?:^(properties\[))(.*?)(?:\]$)/;

    if (regex.test(key)) { 
      obj.properties = obj.properties || {};
      obj.properties[regex.exec(key)[2]] = formData.get(key);
    } else {
      obj[key] = formData.get(key);
    }
  }

  return JSON.stringify(obj);
};

/*
 * Shopify Common JS
 *
 */
if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  }
};

Shopify.setSelectorByValue = function(selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on'+eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  const method = options['method'] || 'post';
  const params = options['parameters'] || {};

  const form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (const key in params) {
    const hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.postLink2 = function(path, options) {
  options = options || {};
  const method = options['method'] || 'post';
  const params = options['parameters'] || {};

  const form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (const key in params) {
    for (const index in params[key]) {
      for (const key2 in params[key][index]) {
        const hiddenField = document.createElement("input");
        hiddenField.setAttribute("type", "hidden");
        hiddenField.setAttribute("name", `${key}[${index}][${key2}]`);
        hiddenField.setAttribute("value", params[key][index][key2]);
        form.appendChild(hiddenField);
      }
    }
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl         = document.getElementById(country_domid);
  this.provinceEl        = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler,this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    var opt       = this.countryEl.options[this.countryEl.selectedIndex];
    var raw       = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  init() {
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus?.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus?.classList.toggle('disabled', value >= max);
    }
  }
}
customElements.define('quantity-input', QuantityInput);

class DrawerCloseButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', () => this.dispatchEvent(new CustomEvent('drawer:force-close', { bubbles: true, cancelable: true, composed: true })));
  }
}
customElements.define('drawer-close-button', DrawerCloseButton);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      open: 'menu-drawer--open',
      opening: 'menu-drawer--opening',
      closing: 'menu-drawer--closing'
    };

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  setClasses(classes) {
    this.classes = classes;
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll('button[data-close]').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

    this.addEventListener('drawer:force-close', (event) => {
      event.stopPropagation();
      this.closeMenuDrawer(event);
    });
  }

  onKeyUp(event) {
    const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
  if (charCode !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement = false) {
    setScrollbarWidth();
    setHeaderBottomPosition();

    if (summaryElement) {
      summaryElement.setAttribute('aria-expanded', true);
      trapFocus(this.mainDetailsToggle, summaryElement);
    }

    setTimeout(() => {
      this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
      document.body.addEventListener('click', this.onBodyClickEvent);
    });
    document.body.classList.add(this.classes.opening);

    this.openAnimation();
    setTimeout(() => {
      this.mainDetailsToggle.setAttribute('open', '');
      setTimeout(() => {
        this.mainDetailsToggle.classList.add('menu-opening');
      }, 10);
    });
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event !== undefined) {
      this.mainDetailsToggle.querySelectorAll('details').forEach(details =>  {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });
      this.mainDetailsToggle.classList.remove('menu-opening');
      this.mainDetailsToggle.classList.add('menu-closing');
      removeTrapFocus(elementToFocus);
      
      document.body.removeEventListener('click', this.onBodyClickEvent);
      document.body.classList.remove(this.classes.open);
      document.body.classList.add(this.classes.closing);
      
      this.closeAnimation(this.mainDetailsToggle);
      this.dispatchEvent(new CustomEvent('close'));
      document.dispatchEvent(new CustomEvent('menudrawer:close'));
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const target = event.currentTarget;
    if (target.classList.contains('noclose')) return;
    
    const detailsElement = target.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus();
    this.closeAnimation(detailsElement);
  }

  openAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      }
      else {
        document.body.classList.remove(this.classes.opening);
        document.body.classList.add(this.classes.open);
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      }
      else {
        if (detailsElement === this.mainDetailsToggle) {
          document.body.classList.remove(this.classes.closing);
          detailsElement.classList.remove('menu-closing');
        }
        detailsElement.removeAttribute('open');

        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }

  onBodyClick(event) {
    if (!this.contains(event.target)) this.closeMenuDrawer(event);
  }
}
customElements.define('menu-drawer', MenuDrawer);

class CartDrawer extends MenuDrawer {
  constructor() {
    super();

    this.setClasses({
      open: 'mini-cart--open',
      opening: 'mini-cart--opening',
      closing: 'mini-cart--closing'
    });
  }

}
customElements.define('cart-drawer', CartDrawer);

class FacetDrawer extends MenuDrawer {
  constructor() {
    super();

    if (this.dataset.desktop == 'true') {
      this.setClasses({
        open: 'facet-horizontal--open',
        opening: 'facet-horizontal--opening',
        closing: 'facet-horizontal--closing'
      });
    }
    else {
      this.setClasses({
        open: 'facet-drawer--open',
        opening: 'facet-drawer--opening',
        closing: 'facet-drawer--closing'
      });
    }
  }
}
customElements.define('facet-drawer', FacetDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();

    this.setClasses({
      open: 'menu-mobile--open',
      opening: 'menu-mobile--opening',
      closing: 'menu-mobile--closing'
    });
  }
}
customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this)
    );
    this.addEventListener('keyup', (event) => {
      const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
      if (charCode === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target.nodeName === 'MODAL-DIALOG') this.hide();
      });
    }
  }

  show(opener) {
    setScrollbarWidth();
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('button');
    if (!this.button) return;

    this.button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(this.button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    
    this.poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!this.poster) return;

    // poster：只负责首次加载
    this.poster.addEventListener('click', (e) => {
       e.stopPropagation();
      this.loadContent();
    });

    // 容器：负责播放 / 暂停切换
    this.addEventListener('click', () => {
      if (this.getAttribute('loaded') !== 'true') return;

      const video = this.querySelector('video');
      if (!video) return;

      video.paused ? video.play() : video.pause();
    });
  }

  loadContent(focus = true) {
  
    //window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      if (content.querySelector('video-section')) {
        const deferredElement = this.appendChild(content);
        if (focus) deferredElement.focus();
        if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
          // force autoplay for safari
          deferredElement.play();
        }
      } else {
        this.setAttribute('loaded', true);
        const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
        if (focus) deferredElement.focus();
        if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
          // force autoplay for safari
          deferredElement.play();
        }
      }
    }
  }
}
customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });
  }

  init() {
    const resizeObserver = new ResizeObserver(() => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(this.slider.clientWidth / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', { detail: {
        currentPage: this.currentPage,
        currentElement: this.sliderItemsToShow[this.currentPage - 1]
      }}));
    }

    if (this.currentPage === 1 && this.currentPage === this.totalPages) {
      this.classList.add('slider--no-buttons');
    } else {
      this.classList.remove('slider--no-buttons');
    }

    if (this.enableSliderLooping) return;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (isFirstSlide || this.isSlideVisible(this.sliderItemsToShow[0])) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (isLastSlide || this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();

    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + (step * this.sliderItemOffset) : this.slider.scrollLeft - (step * this.sliderItemOffset);
    //this.slideScrollPosition = theme.config.rtl ? this.slideScrollPosition * -1 : this.slideScrollPosition;
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }
  
}
customElements.define('slider-component', SliderComponent);

class ThumbnailSlider extends SliderComponent {
  constructor() {
    super();
  }

  init() {
    super.init();

    const variantItems = this.querySelectorAll('.thumbnail-list_item--variant:not(.is-active)');
    this.slider.dataset.mediaCount = this.sliderItems.length - variantItems.length;

    if(this.querySelector('[data-gang-option]')) {
      const inactiveGangItems = this.querySelectorAll('[data-gang-option]:not(.gang__active)');
      this.slider.dataset.mediaCount = this.sliderItems.length - inactiveGangItems.length;
    }

    if (this.slider.dataset.mediaCount < 2) this.classList.add('hidden');
  }
}
customElements.define('thumbnail-slider', ThumbnailSlider);


class ProductGallery extends SliderComponent {
  constructor() {
    super();
  }

  init() {
    super.init();


    const drag = this.dataset.drag;
     // 添加拖动滑动支持
    if(drag == "true"){
      this.addDragScroll();
    }

    this.addEventListener('slideChanged', this.onSliderChanged.bind(this));
  
    const gallerySliderButtons = this.querySelector('.slider-buttons');
    if (gallerySliderButtons && this.querySelector('[data-gang-option]')) {
      if (this.querySelectorAll('.product__media-item.gang__active').length < 2) {
        gallerySliderButtons.classList.add('hidden');
      } else {
        gallerySliderButtons.classList.remove('hidden');
      }
    }

    this.cursor = this.querySelector('.gallery-cursor');
    if (!this.cursor) return;

    const images = this.querySelectorAll('.product__modal-opener--image');
    images.forEach((image) => image.addEventListener('mousemove', this.onMoveHandler.bind(this)));
    images.forEach((image) => image.addEventListener('mouseenter', this.onEnterHandler.bind(this)));
    images.forEach((image) => image.addEventListener('mouseleave', this.onLeaveHandler.bind(this)));

    

  }

onSliderChanged(event) {
  const currentPage = event.detail.currentPage;
  const currentElement = event.detail.currentElement;

  //gtm
  window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({

      event: 'product_slide_changed',
      sliderName: this.dataset.galleryContainer,
      slideIndex: currentPage
    });
  
  
}

  onMoveHandler(event) {
    this.cursor.style.left = `${event.clientX - 32}px`;
    this.cursor.style.top = `${event.clientY - 32}px`;
  }

  onEnterHandler() {
    this.cursor.classList.add('show');
  }

  onLeaveHandler() {
    this.cursor.classList.remove('show');
  }

  update() {
    super.update();

    if (this.currentPageElement && this.pageTotalElement) {
      const currentElement = this.sliderItemsToShow[this.currentPage - 1];
      if (currentElement) {
        this.slider.style.setProperty('--force-image-ratio-percent', currentElement.querySelector('.media').style.getPropertyValue('--image-ratio-percent'));
      }
    }
  }
  
     // 添加拖动滑动支持
  addDragScroll() {
    let isMouseDown = false;
    let startX = 0;
    let scrollLeft = 0;

    this.slider.addEventListener('mousedown', (e) => {
       // 阻止默认的拖动行为，防止拖动图片或其他元素
    e.preventDefault();
      isMouseDown = true;
      startX = e.pageX - this.slider.offsetLeft;
      scrollLeft = this.slider.scrollLeft;
      this.slider.style.cursor = 'grabbing';  // 鼠标变成抓取状态
    });

    this.slider.addEventListener('mouseleave', () => {
      isMouseDown = false;
      this.slider.style.cursor = 'grab';  // 恢复默认光标
    });

    this.slider.addEventListener('mouseup', () => {
      isMouseDown = false;
      this.slider.style.cursor = 'grab';  // 恢复默认光标
    });

    this.slider.addEventListener('mousemove', (e) => {
      if (!isMouseDown) return;

      const x = e.pageX - this.slider.offsetLeft;
      const walk = (x - startX) * 3; // 移动速度，可以根据需要调整
      this.slider.scrollLeft = scrollLeft - walk;
    });
  }

  goToFirstSlide() {
    this.slider.scrollTo({
      left: 0
    });
  }
}
customElements.define('product-gallery', ProductGallery);
class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProgressBar extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 0
    });
  }

  init() {
    setTimeout(() => {
      const quantity = parseInt(this.dataset.quantity);
      this.style.setProperty('--progress-bar-width', `${quantity * 100 / 30}%`);
    }, 1e3);
  }
}
customElements.define('progress-bar', ProgressBar);

class FormState extends HTMLElement {
  constructor() {
    super();

    this.formInputs = this.querySelectorAll('input,select,textarea');
    this.form = this.querySelector('form');

    this.formInputs.forEach((input) => {
      input.addEventListener('input', this.onInputChange.bind(this));
      input.addEventListener('blur', this.onInputChange.bind(this));
    });

    if (this.form) this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
  }

  onInputChange(event) {
    this.handleInputCheck(event.target);
  }

  onSubmitHandler(event) {
    let valid = !0;

    this.formInputs.forEach((input) => {
      if (!this.handleInputCheck(input)) {
        valid = !1;
      }
    });

    if (!valid) {
      event.preventDefault();
      return;
    }
  }

  handleInputCheck(input) {
    if (input.classList.contains('required')) {

      if (input.value.length === 0 || input.value === input.dataset.empty) {
        input.classList.remove('valid');
        input.classList.add('invalid');
        
        return !1;
      }
      else {
        input.classList.remove('invalid');
        input.classList.add('valid');

        return !0;
      }
    }

    return !0;
  }
}
customElements.define('form-state', FormState);

class ListMenuItem extends HTMLElement {
  constructor() {
    super();

    this.listMenuHover = this.closest('menu-drawer').querySelector('list-menu-hover');

    this.addEventListener('mouseenter', () => {
      this.listMenuHover.open({
        title: this.dataset.title,
        url: this.dataset.url,
        image: this.dataset.image,
        description: this.dataset.description
      });
    });
  }
}
customElements.define('list-menu-item', ListMenuItem);

class ListMenuHover extends HTMLElement {
  constructor() {
    super();
  }

  open(obj) {
    let html = '';
    const opened = obj.image.length > 0 || obj.description.length > 0;

    if (opened) {
      html += `<a href="${obj.url}" class="hover-collection${obj.image.length > 0 ? '' : ' hover-collection--no-image'}">`;
        html += '<figure class="hover-collection__image"' + (obj.image.length > 0 ? `style="background-image:url(${obj.image})"` : '') + '></figure>';
        html += '<div class="hover-collection__content">'
          html += `<span class="hover-collection__title h4">${obj.title}</span>`;
          html += obj.description.length > 0 ? `<div class="hover-collection__description">${obj.description}</div>` : '';
        html += '</div>';
      html += '</a>';
    }

    this.innerHTML = html;
  }

  close() {
    this.innerHTML = '';
  }
}
customElements.define('list-menu-hover', ListMenuHover);

class TransparentHeader extends HTMLElement {
  constructor() {
    super();

    const header = document.querySelector('.shopify-section-header');

    header.classList.add('transparent-header');
    if (this.dataset.showSeparator == 'true') {
      header.classList.add('transparent-separator');
    }
  }
}
customElements.define('transparent-header', TransparentHeader);

class AddToCart extends HTMLElement {
  constructor() {
    super();

    this.miniCart = document.querySelector('mini-cart');
    this.addEventListener('click', this.onClickHandler.bind(this));
  }

  onClickHandler() {
    const variantId = this.dataset.variantId;


    /* 自定义添加properties,配合gift function的折扣 */
    const key = this.dataset.attributeKey;
    const value = this.dataset.attributeValue;
     const isLimited = this.dataset.limit === 'true';

    const properties = {};

    if (key) {
      properties[key] = value;
    }
    console.log('属性值',properties);

      // 检查是否已领取
    if (isLimited) {
      const claimedKey = `gift_claimed_${variantId}`;
      const claimed_info = document.querySelector('.gift-claimed-info');   

      if (sessionStorage.getItem(claimedKey)) {
        if (claimed_info) {
          claimed_info.style.display = 'block';

          // 5 秒后隐藏
          setTimeout(() => {
            claimed_info.style.display = 'none';
          }, 5000);
        }

        return;
      }
    }

    if (variantId) {
      if (document.body.classList.contains('template-cart') || !theme.shopSettings.cartDrawer) {
        Shopify.postLink(theme.routes.cart_add_url, {
          parameters: {
            id: variantId,        
            quantity: 1,
            ...(Object.keys(properties).length ? { properties } : {})
          },
        });

        if (isLimited) {
          sessionStorage.setItem(`gift_claimed_${variantId}`, 'true');
        }

        return;
      }

      this.setAttribute('disabled', true);
      this.classList.add('loading');
      const sections = this.miniCart ? this.miniCart.getSectionsToRender().map((section) => section.id) : [];

      const body = JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sections,
        sections_url: window.location.pathname,
        ...(Object.keys(properties).length ? { properties } : {})
      });

      fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
        .then((response) => response.json())
        .then((parsedState) => {
          if (parsedState.status === 422) {
             document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
                detail: {
                  errorMessage: parsedState.description
                }
              }));
           }
           else {

             // 加购成功后记录
            if (isLimited) {
              sessionStorage.setItem(`gift_claimed_${variantId}`, 'true');
            }

            this.miniCart && this.miniCart.renderContents(parsedState);

             document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
              detail: {
                product: parsedState
              }
            }));
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.classList.remove('loading');
          this.removeAttribute('disabled');
        });
    }
  }
}
customElements.define('add-to-cart', AddToCart);


class PriceMoney extends HTMLElement {
  constructor() {
    super();

    if (this.shouldInit()) {
      this.init();
    }
  }

  shouldInit() {
    if (document.body.dataset.priceSuperscript === undefined) {
      return false;
    }  

    const moneyFormat = theme.shopSettings.moneyFormat.toLowerCase();
    if (moneyFormat.indexOf('class=') !== -1) {
      return false;
    }
    
    return true;
  }

  init() {
    const currencies_using_comma_decimals = 'ANG,ARS,BRL,BYN,BYR,CLF,CLP,COP,CRC,CZK,DKK,EUR,HRK,HUF,IDR,ISK,MZN,NOK,PLN,RON,RUB,SEK,TRY,UYU,VES,VND'.split(',');
    const symbol = theme.shopSettings.moneyFormat.replace(/\{{.*}}/, '').trim();
    const bdi = this.querySelector('bdi');
    const price = bdi.textContent.replace(theme.shopSettings.isoCode,'');
    let html = price.replace(' ', '');
    let money_symbol_decimal = '.';
    if (currencies_using_comma_decimals.includes(theme.shopSettings.isoCode)) {
      money_symbol_decimal = ',';
    }
    if (price.includes(money_symbol_decimal)) {
      if (price.lastIndexOf(symbol) + symbol.length === price.length) {
        const price_without_symbol = price.slice(0, price.lastIndexOf(symbol));
        const price_without_decimal = price.slice(0, price.lastIndexOf(money_symbol_decimal));
        const decimal = price_without_symbol.replace(price_without_decimal, '').trim();
        html = html.replace(decimal, `<sup class="price__suffix">${decimal}</sup>`);
      }
      else {
        const price_without_decimal = price.slice(0, price.lastIndexOf(money_symbol_decimal));
        const decimal = price.replace(price_without_decimal, '').trim();
        html = html.replace(decimal, `<sup class="price__suffix">${decimal}</sup>`);
      }
    }
    html = html.replace(symbol, `<span class="price__prefix">${symbol}</span>`);
    if(theme.shopSettings.currencyCode){
       html = html + " " + theme.shopSettings.isoCode;
    }
    bdi.innerHTML = html;
  }
}
customElements.define('price-money', PriceMoney);

class SlideshowComponent extends HTMLElement {
  constructor() {
    super();

    this.elements = {
      small: this.querySelector('.slideshow__left'),
      content: this.querySelector('.slideshow__content'),
      large: this.querySelector('.slideshow__right')
    }
    this.settings = {
      autorotate: this.dataset.autorotate == 'true',
      prevNextButtons: this.dataset.arrow,
      pageDots: this.dataset.dots,
      autorotateSpeed: parseInt(this.dataset.autorotateSpeed),
      slidesSmall: this.elements.small?.querySelectorAll('.slideshow__image'),
      slidesLarge: this.elements.large.querySelectorAll('.slideshow__image')
    };

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600
    });
    this.matchMedia();
  }

  init() {
    setTimeout(() => {
      if (this.elements.small) {
      this.flickitySmall = new Flickity(this.elements.small, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: false,
        pageDots: false,
        wrapAround: true,
        draggable: false,
        setGallerySize: false,
        autoPlay: this.settings.autorotate ? this.settings.autorotateSpeed : false,
        dragThreshold: 20
      });
      }
      if (this.elements.content) {
      this.flickityContent = new Flickity(this.elements.content, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: true,
        pageDots: true,
        wrapAround: true,
        draggable: theme.config.mqlSmall ? true : false,
        autoPlay: this.settings.autorotate ? this.settings.autorotateSpeed : false,
        asNavFor: this.elements.small,
        adaptiveHeight: true,
        arrowShape: 'M29.043 19.756l-28.125 28.125c-0.605 0.606-0.918 1.406-0.918 2.031s0.305 1.599 0.916 2.209l28.125 28.125c1.221 1.221 3.199 1.221 4.418 0s1.221-3.199 0-4.418l-22.793-22.793h86.209c1.727 0 3.125-1.398 3.125-2.949 0-1.727-1.398-3.125-3.125-3.125h-86.211l22.793-22.793c1.221-1.221 1.221-3.199 0-4.418s-3.203-1.217-4.414 0.006z',
        on: {
          ready: () => {
            const prevNextButtons = this.querySelectorAll('.flickity-button');
            if (prevNextButtons) {
              prevNextButtons.forEach((button) => {
                button.setAttribute('tabindex', '-1');
              });
            }
          }
        },
        dragThreshold: 20
      });
      }

      this.flickityLarge = new Flickity(this.elements.large, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: this.settings.prevNextButtons,
        pageDots: this.settings.pageDots,
        wrapAround: true,
        draggable: true,
        autoPlay: this.settings.autorotate ? this.settings.autorotateSpeed : false,
        setGallerySize: false,
        asNavFor: this.elements.content,
        dragThreshold: 20
      });
      
        this.bindEvents();   
      
    });
  }

  bindEvents() {
    this.flickitySmall?.on('scroll', () => {
      this.flickitySmall.slides.forEach((slide, i) => {
        const flickity = this.flickitySmall;
        const image = this.settings.slidesSmall[i];
        let x = 0;

        if (image) {
          if( 0 === i ) {
            x = Math.abs( flickity.x ) > flickity.slidesWidth ? ( flickity.slidesWidth + flickity.x + flickity.slides[flickity.slides.length - 1 ].outerWidth + slide.target ) : ( slide.target + flickity.x );
          }
          else if( i === flickity.slides.length - 1 ) {
            x = Math.abs( flickity.x ) + flickity.slides[i].outerWidth < flickity.slidesWidth ? ( slide.target - flickity.slidesWidth + flickity.x - flickity.slides[i].outerWidth ) : ( slide.target + flickity.x );
          }
          else {
            x = slide.target + flickity.x;
          }
          
          if (!theme.config.isTouch && !theme.config.rtl) {
            image.style.transform = 'translateX(' + x * ( -1 / 2 ) + 'px)';
          }
        }
      });
    });

    this.flickityLarge.on('scroll', () => {
      this.flickityLarge.slides.forEach((slide, i) => {
        const flickity = this.flickityLarge;
        const image = this.settings.slidesLarge[i];
        let x = 0;

        if (image) {
          if( 0 === i ) {
            x = Math.abs( flickity.x ) > flickity.slidesWidth ? ( flickity.slidesWidth + flickity.x + flickity.slides[flickity.slides.length - 1 ].outerWidth + slide.target ) : ( slide.target + flickity.x );
          }
          else if( i === flickity.slides.length - 1 ) {
            x = Math.abs( flickity.x ) + flickity.slides[i].outerWidth < flickity.slidesWidth ? ( slide.target - flickity.slidesWidth + flickity.x - flickity.slides[i].outerWidth ) : ( slide.target + flickity.x );
          }
          else {
            x = slide.target + flickity.x;
          }

          if (!theme.config.isTouch && !theme.config.rtl) {
            image.style.transform = 'translateX(' + x * ( -1 / 2 ) + 'px)';
          }
        }
      });
    });

    if(this.elements.content){
    this.elements.content.querySelector('.flickity-button.next').addEventListener('click', () => {
      this.flickitySmall && this.flickitySmall.next();
      this.stopPlayer();
    });

    this.elements.content.querySelector('.flickity-button.previous').addEventListener('click', () => {
      this.flickitySmall && this.flickitySmall.previous();
      this.stopPlayer();
    });

    this.elements.content.querySelectorAll('.flickity-page-dot').forEach((button) => {
      button.addEventListener('click', () => {
        this.stopPlayer();
      });
    });
    }

    let currentIndex = null,
      swipeDirection = null;
  
    // Detect swipe direction
    this.flickityLarge.on('dragMove',  (_event, _pointer, moveVector) => {
      currentIndex = this.flickityLarge.selectedIndex;
      swipeDirection = this.getSwipeDirection(moveVector);

      this.stopPlayer();
    });
    
    // Do stuff based on a successful swipe and it's direction
    if (this.elements.small) {
    this.flickityLarge.on('dragEnd', () => {
      if (this.flickityLarge.selectedIndex !== currentIndex) {
        
        if (swipeDirection === 'left') {
          this.flickitySmall.next();
        }
        else {
          this.flickitySmall.previous();
        }
      }
    });
    }

    // Handle lazy video loading on slide change
    const handleSlideVideos = () => {
      const selectedIndex = this.flickityLarge.selectedIndex;
      // Pause all videos
      this.elements.large.querySelectorAll('video').forEach(video => {
        video.pause();
      });
      this.elements.large.querySelectorAll('video-section[data-type="youtube"] iframe').forEach(iframe => {
        iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      this.elements.large.querySelectorAll('video-section[data-type="vimeo"] iframe').forEach(iframe => {
        iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
      });
      
      // Find current slide
      const currentSlide = this.elements.large.querySelector(`.slideshow__slide[data-slide-index="${selectedIndex}"]`);
      if (!currentSlide) return;

      const mp4Sections = currentSlide.querySelectorAll('video-section[data-type="mp4"]');

      mp4Sections.forEach(section => {
        const video = section.querySelector('video');
        if (!video) return;

        // 判断当前 video-section 是否可见
        const isVisible = section.offsetParent !== null; 

        if (isVisible) {
          if (video.preload === 'none') {
            video.preload = 'auto';
            video.load();
          }
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });

      const youtubeSection = currentSlide.querySelector('video-section[data-type="youtube"]');
      if (youtubeSection) {
        if (youtubeSection.dataset.initMode === 'manual' && !youtubeSection.player) {
          youtubeSection.removeAttribute('data-init-mode');
          youtubeSection.init();
        } else {
          const iframe = youtubeSection.querySelector('iframe');
          if (iframe) {
            iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
          } else if (youtubeSection.player && typeof youtubeSection.player.playVideo === 'function') {
            youtubeSection.player.playVideo();
          }
        }
        return;
      }

      const vimeoSection = currentSlide.querySelector('video-section[data-type="vimeo"]');
      if (vimeoSection) {
        if (vimeoSection.dataset.initMode === 'manual' && !vimeoSection.player) {
          vimeoSection.removeAttribute('data-init-mode');
          vimeoSection.init();
        } else {
          const iframe = vimeoSection.querySelector('iframe');
          if (iframe) {
            iframe.contentWindow?.postMessage('{"method":"play"}', '*');
          } else if (vimeoSection.player && typeof vimeoSection.player.play === 'function') {
            vimeoSection.player.play();
          }
        }
      }
    };
    
    this.flickityLarge.on('select', handleSlideVideos);
    // Init call for first slide
    setTimeout(handleSlideVideos, 100);

  }

  stopPlayer() {
    if (this.settings.autorotate) {
      this.flickitySmall && this.flickitySmall.stopPlayer();
    }
  }

  getSwipeDirection(moveVector) {
    return moveVector.x > 0 ? 'right' : 'left';
  }

  matchMedia() {
    document.addEventListener('matchSmall', () => {
      this.unload();
      this.init();
    });

    document.addEventListener('unmatchSmall', () => {
      this.unload();
      this.init();
    });
  }

  unload() {
    if (this.flickitySmall && typeof this.flickitySmall.destroy === 'function') {
      this.flickitySmall.destroy();
    }
    if (this.flickityContent && typeof this.flickityContent.destroy === 'function') {
      this.flickityContent.destroy();
    }
    if (this.flickityLarge && typeof this.flickityLarge.destroy === 'function') {
      this.flickityLarge.destroy();
    }
  }
}
customElements.define('slideshow-component', SlideshowComponent);

class UseAnimate extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 0
    });
  }

  init() {
    this.setAttribute('animate', '');
  }
}
customElements.define('use-animate', UseAnimate);

class AnimateSticky extends HTMLElement {
  constructor() {
    super();

    this.targetElement = null;
  }

  connectedCallback() {

    this.targetElement = document.querySelector('.product .product-form');
    if (!this.targetElement) {
      return;
    }
    
    this.onScrollHandler = this.onScroll.bind(this);

    window.addEventListener('scroll', this.onScrollHandler, false);
    this.onScrollHandler();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScroll() {
     if (!this.targetElement) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    const triggerOffset = this.getOffsetTop(this.targetElement);

      if (scrollTop > triggerOffset) {
        window.requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.requestAnimationFrame(this.reset.bind(this));
      }
  }

  reveal() {
    this.setAttribute('animate', '');
  }

  reset() {
    this.removeAttribute('animate');
  }

  getOffsetTop(element) {
    let offsetTop = 0;
    while(element) {
      offsetTop += element.offsetTop;
      element = element.offsetParent;
    }
    return offsetTop;
  }
}
customElements.define('animate-sticky', AnimateSticky);

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    this.parent = this.closest('.product-countdown');
    this.date = this.parseToDate(this.dataset.date).getTime();
    
    if (isNaN(this.date)) {
      this.unload();
      return;
    }

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });
  }

  parseToDate(input) {
    if (input.includes("T")) {
      return new Date(input);
    }
    
    const [month, day, year] = input.split('-');
    return new Date(`${year}-${month}-${day}`);
  }

  init() {
    this.timer();
    this.dataset.interval = setInterval(this.timer.bind(this), 1000);
  }

  timer() {
    const now = new Date(),
      countTo = new Date(this.date),
      timeDifference = (countTo - now);

    if (timeDifference < 0) {
      this.unload();
      return;
    }

    const secondsInADay = 60 * 60 * 1000 * 24,
      secondsInAHour = 60 * 60 * 1000;

    const days = Math.floor(timeDifference / (secondsInADay) * 1);
    const hours = Math.floor((timeDifference % (secondsInADay)) / (secondsInAHour) * 1);
    const mins = Math.floor(((timeDifference % (secondsInADay)) % (secondsInAHour)) / (60 * 1000) * 1);
    const secs = Math.floor((((timeDifference % (secondsInADay)) % (secondsInAHour)) % (60 * 1000)) / 1000 * 1);

    if (this.dataset.compact == 'true') {
      const dayHTML = days > 0 ? `<div class="countdown__item"><span>${days}${theme.dateStrings.d}</span></div>` : '';
      const otherHTML = `<div class="countdown__item"><span>${hours > 9 ? hours : '0' + hours}:${mins > 9 ? mins : '0' + mins}:${secs > 9 ? secs : '0' + secs}</span></div>`;
  
      this.innerHTML = dayHTML + otherHTML;
    }
    else {
      const dayHTML = days > 0 ? `<div class="countdown__item"><span>${days}</span> ${days == 1 ? theme.dateStrings.day : theme.dateStrings.days}</div>` : '';
      const hourHTML = `<div class="countdown__item"><span>${hours}</span> ${hours == 1 ? theme.dateStrings.hour : theme.dateStrings.hours}</div>`;
      const minHTML = `<div class="countdown__item"><span>${mins}</span> ${mins == 1 ? theme.dateStrings.minute : theme.dateStrings.minutes}</div>`;
      const secHTML = `<div class="countdown__item"><span>${secs}</span> ${secs == 1 ? theme.dateStrings.second : theme.dateStrings.seconds}</div>`;

      this.innerHTML = dayHTML + hourHTML + minHTML + secHTML;
    }
  }

  unload() {
    if (this.dataset.interval) {
      clearInterval(this.dataset.interval);
    }

    this.classList.add('hidden');
    if (this.parent) {
      this.parent.classList.add('hidden');
    }
  }
}
customElements.define('countdown-timer', CountdownTimer);

class RelatedButtons extends HTMLElement {
  constructor() {
    super();

    if(!document.querySelector(this.dataset.scrollto)) {
      this.classList.add('hidden');
      return;
    }

    window.addEventListener('scroll', this.checkListener.bind(this));
    this.querySelectorAll('a').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  checkListener() {
    const element = document.querySelector(this.dataset.scrollto);
    const show = window.scrollY >= element.offsetTop - element.clientHeight;

    if (show) {
      this.classList.add('is-flipped');
    } else {
      this.classList.remove('is-flipped');
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.target;
    const scrollIntoView = document.querySelector(target.getAttribute('href'));

    scrollIntoView.scrollIntoView({behavior: "smooth"});
  }
}
customElements.define('related-buttons', RelatedButtons);

class ProductRecentlyViewed extends HTMLElement {
  constructor() {
    super();
    
    // We save the product ID in local storage to be eventually used for recently viewed section
    if (isStorageSupported('local')) {
      const productId = parseInt(this.dataset.productId);
      const cookieName = 'beyours:recently-viewed';
      const items = JSON.parse(window.localStorage.getItem(cookieName) || '[]');

      // We check if the current product already exists, and if it does not, we add it at the start
      if (!items.includes(productId)) {
        items.unshift(productId);
      }

      // By keeping only the 4 most recent
      window.localStorage.setItem(cookieName, JSON.stringify(items.slice(0, 5)));
    }
  }
}
customElements.define('product-recently-viewed', ProductRecentlyViewed);

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600
    });
  }

  init() {
    fetch(this.dataset.url + this.getQueryString())
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('recently-viewed-products');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;

          // 新内容加载后，重新绑定点击 + 曝光逻辑
         initCardTracking(this);
        }
      })
      .catch(e => {
        console.error(e);
      });
  }

  getQueryString() {
    const cookieName = 'beyours:recently-viewed';
    const items = JSON.parse(window.localStorage.getItem(cookieName) || "[]");
    if (this.dataset.productId && items.includes(parseInt(this.dataset.productId))) {
      items.splice(items.indexOf(parseInt(this.dataset.productId)), 1);
    }
    return items.map((item) => "id:" + item).slice(0, 4).join(" OR ");
  }
}
customElements.define('recently-viewed-products', RecentlyViewedProducts);

class VideoSection extends HTMLElement {
  constructor() {
    super();

    this.background = this.dataset.initMode !== 'template';
    this.loop = this.dataset.loop;

    if (this.background) {
      theme.initWhenVisible({
        element: this,
        callback: this.init.bind(this),
        threshold: 600
      });
    }
    else {
      this.init();
    }
  }

  init() {
    this.parentSelector = this.dataset.parent || '.media-wrapper';
    this.parent = this.closest(this.parentSelector);

    switch(this.dataset.type) {
      case 'youtube':
        this.initYoutubeVideo();
        break;

      case 'vimeo':
        this.initVimeoVideo();
        break;

      case 'mp4':
        this.initMp4Video();
        break;
    }
  }

  initYoutubeVideo() {
    this.setAsLoading();
    this.loadScript('youtube').then(this.setupYoutubePlayer.bind(this));
  }

  initVimeoVideo() {
    this.setAsLoading();
    this.loadScript('vimeo').then(this.setupVimeoPlayer.bind(this));
  }

  initMp4Video() {
    const player = this.querySelector('video');

    if (player) {
      const promise = player.play();

      // Edge does not return a promise (video still plays)
      if (typeof promise !== 'undefined') {
        promise.then(function() {
          // playback normal
        }).catch(function() {
          player.setAttribute('controls', '');
        });
      }
    }
  }

  loadScript(videoType) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      document.body.appendChild(script);
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.src = videoType === 'youtube' ? '//www.youtube.com/iframe_api' : '//player.vimeo.com/api/player.js';
    });
  }

  setAsLoading() {
    this.parent.setAttribute('loading', true);
  }

  setAsLoaded() {
    this.parent.removeAttribute('loading');
    this.parent.setAttribute('loaded', true);
  }

  setupYoutubePlayer() {
    const videoId = this.dataset.videoId;
    
    const playerInterval = setInterval(() => {
      if (window.YT) {
        window.YT.ready(() => {
          const element = document.createElement('div');
          this.appendChild(element);

          this.player = new YT.Player(element, {
            videoId: videoId,
            playerVars: {
              showinfo: 0,
              controls: !this.background,
              fs: !this.background,
              rel: 0,
              height: '100%',
              width: '100%',
              iv_load_policy: 3,
              html5: 1,
              loop: this.loop == '1' ? 1 : 0,
              playlist: videoId,
              playsinline: 1,
              modestbranding: 1,
              disablekb: 1
            },
            events: {
              onReady: this.onYoutubeReady.bind(this),
              onStateChange: this.onYoutubeStateChange.bind(this)
            }
          });
          clearInterval(playerInterval);
        });
      }
    }, 50);
  }

  onYoutubeReady() {
    this.iframe = this.querySelector('iframe'); // iframe once YT loads
    this.iframe.setAttribute('tabindex', '-1');

    this.background && this.player.mute();
    if (typeof this.player.playVideo === 'function') this.player.playVideo();

    this.setAsLoaded();

    // pause when out of view
    const observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        entry.isIntersecting ? this.youtubePlay() : this.youtubePause();
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    observer.observe(this.iframe);
  }

  onYoutubeStateChange(event) {
    switch (event.data) {
      case -1: // unstarted
        // Handle low power state on iOS by checking if
        // video is reset to unplayed after attempting to buffer
        if (this.attemptedToPlay) {
          this.setAsLoaded();
          this.closest('.banner').classList.add('video-interactable');
        }
        break;
      case 0: // ended, loop it
        this.youtubePlay();
        break;
      case 1: // playing
        this.setAsLoaded();
        break;
      case 3: // buffering
        this.attemptedToPlay = true;
        break;
    }
  }

  youtubePlay() {
    if (this.background && this.player && typeof this.player.playVideo === 'function') {
      this.player.playVideo();
    }
  }

  youtubePause() {
    if (this.background && this.player && typeof this.player.pauseVideo === 'function') {
      this.player.pauseVideo();
    }
  }

  setupVimeoPlayer() {
    const videoId = this.dataset.videoId;

    const playerInterval = setInterval(() => {
      if (window.Vimeo) {
        this.player = new Vimeo.Player(this, {
          id: videoId,
          autoplay: true,
          autopause: false,
          background: this.background,
          controls: !this.background,
          loop: this.loop == '1' ? true : false,
          playlist: videoId,
          height: '100%',
          width: '100%'
        });
        this.player.ready().then(this.onVimeoReady.bind(this));

        clearInterval(playerInterval);
      }
    }, 50);
  }

  onVimeoReady() {
    this.iframe = this.querySelector('iframe');
    this.iframe.setAttribute('tabindex', '-1');

    this.background && this.player.setMuted(true);

    this.setAsLoaded();

    // pause when out of view
    const observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.vimeoPlay();
        } else {
          this.vimeoPause();
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    observer.observe(this.iframe);
  }

  vimeoPlay() {
    if (this.background && this.player && typeof this.player.play === 'function') {
      this.player.play();
    }
  }

  vimeoPause() {
    if (this.background && this.player && typeof this.player.pause === 'function') {
      this.player.pause();
    }
  }
}
customElements.define('video-section', VideoSection);

class BundleProduct extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      souldout: 'price--sold-out',
      onsale: 'price--on-sale',
      nocompare: 'price--no-compare'
    };

    this.addToCart = this.querySelector('add-to-cart');
    this.price = this.querySelector('.price');
    this.variants = this.querySelector('select');
    if (this.variants) {
      this.variants.addEventListener('change', this.onSelectChange.bind(this));
    }
  }

  onSelectChange(event) {
    const variants = event.target;
    const variant = variants.options[variants.selectedIndex];
    const compare_at_price = parseInt(variant.dataset.compare_at_price || 0);
    const price = parseInt(variant.dataset.price || 0);
    const available = variant.dataset.available === 'true';
    const price_varies = variants.dataset.price_varies === 'true';
    const compare_at_price_varies = variants.dataset.compare_at_price_varies === 'true';

    // Remove classes
    this.price.classList.remove(this.classes.souldout);
    this.price.classList.remove(this.classes.onsale);
    this.price.classList.remove(this.classes.nocompare);

    // Add classes
    if (available === false) {
      this.price.classList.add(this.classes.souldout);
    }
    else if (compare_at_price > price && available) {
      this.price.classList.add(this.classes.onsale);
    }
    if (price_varies == false && compare_at_price_varies) {
      this.price.classList.add(this.classes.nocompare);
    }

    // Change price
    const price__regular = this.querySelector('.price__regular');
    price__regular.querySelector('.price-item--regular').innerHTML = `<price-money><bdi>${theme.Currency.formatMoney(price, theme.shopSettings.moneyFormat)}</bdi></price-money>`;

    const price__sale = this.querySelector('.price__sale');
    price__sale.querySelector('.price-item--regular').innerHTML = `<price-money><bdi>${theme.Currency.formatMoney(compare_at_price, theme.shopSettings.moneyFormat)}</bdi></price-money>`;
    price__sale.querySelector('.price-item--sale').innerHTML = `<price-money><bdi>${theme.Currency.formatMoney(price, theme.shopSettings.moneyFormat)}</bdi></price-money>`;

    // Change variant id
    if (this.addToCart) {
      this.addToCart.dataset.variantId = variant.value;
    }
  }
}
customElements.define('bundle-product', BundleProduct);

class BundleProducts extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
    };

    this.miniCart = document.querySelector('mini-cart');
    this.button = this.querySelector('button');
    if (this.button) this.button.addEventListener('click', this.onButtonClick.bind(this));

    this.blocks = this.querySelectorAll('[data-block-id]');
    this.blocks.forEach((block) => block.addEventListener('mouseenter', this.onEnterHandler.bind(this)));
    this.blocks.forEach((block) => block.addEventListener('mouseleave', this.onLeaveHandler.bind(this)));
  }

  onEnterHandler(event) {
    this.classList.add(this.classes.hover);

    const target = event.target;
    const blockId = target.dataset.blockId;
    this.blocks.forEach((block) => {
      if (target.nodeName === 'BUNDLE-PRODUCT') {
        if (block.nodeName === 'BUNDLE-PRODUCT') {
          block.classList.add(this.classes.active);
          return;
        }
      }
      if (block.dataset.blockId === blockId) {
        block.classList.add(this.classes.active);
      }
    });
  }

  onLeaveHandler() {
    this.classList.remove(this.classes.hover);
    this.blocks.forEach((block) => block.classList.remove(this.classes.active));
  }

  onButtonClick(event) {
    event.preventDefault();

    const ids = this.querySelectorAll('[name="id"]');
    const items = {
      items: [...ids].map((e => e.value)).map((e => ({
        id: e,
        quantity: 1
      })))
    };

    if (document.body.classList.contains('template-cart') || !theme.shopSettings.cartDrawer) {
      Shopify.postLink2(theme.routes.cart_add_url, {
        parameters: {
          ...items
        }
      });
      return;
    }

    this.handleErrorMessage();

    this.button.setAttribute('disabled', true);
    this.button.classList.add('loading');
    const sections = this.miniCart ? this.miniCart.getSectionsToRender().map((section) => section.id) : [];

    const body = JSON.stringify({
      ...items,
      sections: sections,
      sections_url: window.location.pathname
    });

    fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }
        
        
        if (response.status === 422) {
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: response.description
            }
          }));
        }
        else {
          this.miniCart && this.miniCart.renderContents(response);

          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: {
              product: response
            }
          }));
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.button.classList.remove('loading');
        this.button.removeAttribute('disabled');
      });
  }

  
  handleErrorMessage(errorMessage = false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    
    if (this.errorMessageWrapper) {
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
    else {
      if (errorMessage) {
        alert(errorMessage);
      }
    }
  }
}
customElements.define('bundle-products', BundleProducts);


class SelectWrapper extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });

    this.select = this.querySelector('select');
    if (this.select) {
      this.select.addEventListener('change', this.handleSelectChange.bind(this));
    }
  }

  init() {
    const style = window.getComputedStyle(this.select);
    const value = this.select.options[this.select.selectedIndex].text;

    const text = document.createElement('span');
    text.style.fontFamily = style.fontFamily;
    text.style.fontSize = style.fontSize;
    text.style.fontWeight = style.fontWeight;
    text.style.visibility = 'hidden';
    text.style.position = 'absolute';
    text.innerHTML = value;

    document.body.appendChild(text);
    const width = text.clientWidth;
    
    this.style.setProperty('--width', `${width}px`);
    text.remove();
  }

  handleSelectChange() {
    this.init();
  }
}
customElements.define('select-wrapper', SelectWrapper);


class AccordionTab extends HTMLElement {
  constructor() {
    super();

    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.querySelector('.accordion__content');

    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });
  }

  init() {
    this.summary.addEventListener('click', (e) => this.onClick(e));
  }

  onClick(e) {
    e.preventDefault();

    this.details.style.overflow = 'hidden';
    if (this.isClosing || !this.details.open) {
      this.open();
    }
    else if (this.isExpanding || this.details.open) {
      this.shrink();
    }
  }

  shrink() {
    this.isClosing = true;
    
    const summaryStyle = window.getComputedStyle(this.summary);
    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + parseInt(summaryStyle.marginTop)}px`;
    
    if (this.animation) {
      this.animation.cancel();
    }
    
    this.animation = this.details.animate({
      height: [startHeight, endHeight]
    }, {
      duration: 250,
      easing: 'ease'
    });
    
    this.animation.onfinish = () => this.onAnimationFinish(false);
    this.animation.oncancel = () => this.isClosing = false;
  }

  open() {
    this.details.style.height = `${this.details.offsetHeight}px`;
    this.details.open = true;
    window.requestAnimationFrame(() => this.expand());
  }

  expand() {
    this.isExpanding = true;

    const summaryStyle = window.getComputedStyle(this.summary);
    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + parseInt(summaryStyle.marginTop) + this.content.offsetHeight}px`;
    
    if (this.animation) {
      this.animation.cancel();
    }
    
    this.animation = this.details.animate({
      height: [startHeight, endHeight]
    }, {
      duration: 400,
      easing: 'ease-out'
    });
    this.animation.onfinish = () => this.onAnimationFinish(true);
    this.animation.oncancel = () => this.isExpanding = false;
  }

  onAnimationFinish(open) {
    this.details.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.details.style.height = this.details.style.overflow = '';
  }
}
customElements.define('accordion-tab', AccordionTab);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    new IntersectionObserver(this.handleIntersection.bind(this), {rootMargin: '0px 0px 600px 0px'}).observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          // 新内容加载后，重新绑定点击 + 曝光逻辑
          initCardTracking(this);
        }
      })
      .catch(e => {
        console.error(e);
      });
  }
}
customElements.define('product-recommendations', ProductRecommendations);

class HeaderMenuToggle extends HTMLElement {
  constructor() {
    super();
    
    this.header = document.querySelector('.shopify-section-header');
    this.addEventListener('click', this.onButtonClick.bind(this));
  }

  onButtonClick() {
    this.header.classList.toggle('shopify-section-header-menu-open');
  }
}
customElements.define('header-menu-toggle', HeaderMenuToggle);

class LocalizationForm extends HTMLElement {
  constructor() {
    super();

    this.elements = {
      input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
      button: this.querySelector('button'),
      panel: this.querySelector('ul'),
    };
    
    this.elements.button.addEventListener('click', this.openSelector.bind(this));
    this.elements.button.addEventListener('focusout', this.closeSelector.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.querySelectorAll('a').forEach(item => item.addEventListener('click', this.onItemClick.bind(this)));
  }

  hidePanel() {
    this.elements.button.setAttribute('aria-expanded', 'false');
    this.elements.panel.setAttribute('hidden', true);

    if (this.classList.contains('on-header')) {
      document.body.classList.remove('localization--open');
    }
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.hidePanel();
    // this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    this.elements.input.value = event.currentTarget.dataset.value;
    if (form) form.submit();
  }

  openSelector() {
    // this.elements.button.focus();
    this.elements.panel.toggleAttribute('hidden');
    this.elements.button.setAttribute('aria-expanded', (this.elements.button.getAttribute('aria-expanded') === 'false').toString());

    if (this.classList.contains('on-header')) {
      document.body.classList.toggle('localization--open');
    }
  }

  closeSelector(event) {
    const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === 'BUTTON';
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }
}
customElements.define('localization-form', LocalizationForm);



class ScrollToTop extends HTMLElement {
  constructor() {
      super();
      this.init();
  }
  init() {
      this.bindEvent();
      this.addScrollTopEvent();
  }
  bindEvent() {
      window.addEventListener('scroll', this.debounce((event)=> {
          if(window.scrollY > window.innerHeight) {
              this.style.display = "flex";
          } else {
              this.style.display = "none";
          }
      }, 300));
  }
  addScrollTopEvent() {
      this.addEventListener('click', (event)=> {
          window.scroll({ top: 0, behavior: 'smooth' });           
      });
  }
  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
customElements.define('scroll-to-top', ScrollToTop);

class FlashSaleComponent extends HTMLElement {
  constructor() {
    super();

    if (this.dataset.slider == 'true') {
      this.slider = this.querySelector('.product-list');
      theme.initWhenVisible({
        element: this,
        callback: this.init.bind(this),
        threshold: 600
      });
      window.addEventListener('scroll', debounce(()=> {
        if (this.flickity) {
          this.isWindow();
        }
      }, 600));
    }
  }

  init() {
    setTimeout(() => {
      this.flickity = new Flickity(this.slider, {
        groupCells: 1,
        accessibility: false,
        rightToLeft: theme.config.rtl,
        adaptiveHeight: true,
        prevNextButtons: true,
        arrowShape: 'M14.453 30.078q-0.391 1.172-3.125 16.602t-2.539 15.625 16.406 3.125 16.406 2.734 0.195-2.148l0.391-1.563-13.281-2.344q-13.281-2.344-13.281-2.734t2.734-2.344 5.078-3.125q3.906-2.734 9.18-4.688t10.352-2.734q3.516-0.391 8.789-0.391t8.789 0.391q15.625 2.344 28.125 13.281l3.125 2.344 1.563-1.172q1.172-1.172 1.172-1.563t-3.32-3.125-5.273-4.297q-5.078-3.125-11.133-5.664t-11.914-3.711q-3.906-0.781-10.938-0.781t-10.938 0.781q-6.25 1.172-13.281 4.297t-11.719 6.641q-1.563 1.563-1.758 1.367t1.758-12.305l2.344-12.109q0-0.391-1.953-0.781h-1.563q-0.391 0-0.391 0.391z',
        pageDots: false,
        wrapAround: false,
        draggable: true,
        dragThreshold: 15,
        selectedAttraction: 0.03,
        cellAlign: 'left',
        contain: true,
        autoPlay: 5000
      });
    });
  }
  
  isWindow() {
    if (window.innerWidth > 750) {
      this.flickity && this.flickity.stopPlayer();
      return;
    } else {
      this.flickity && this.flickity.playPlayer();      
    }
    let boxOffset = this.getBoundingClientRect();
    if (window.innerHeight - boxOffset.height - 150 > boxOffset.top && boxOffset.top > 100) {
      // this.flickity.next();
      this.flickity.unpausePlayer();
    } else {
      this.flickity.pausePlayer();
    }
  }
}
customElements.define('flash-sale-component', FlashSaleComponent);

class LoopCountdownTimer extends HTMLElement {
  constructor() {
    super();


    // 设置倒计时为24小时
    this.duration = 24 * 60 * 60 * 1000; // 24小时，单位为毫秒

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });
  }

  init() {
    // 启动倒计时
    this.startTimer();
    this.dataset.interval = setInterval(this.startTimer.bind(this), 1000);
  }

  startTimer() {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // 获取今天零点时间
    const timeDifference = this.duration - (now - startOfDay);

    if (timeDifference <= 0) {
      // 如果时间已经超过24小时，重新从零点开始
      this.resetTimer();
      return;
    }

    // 计算剩余的天、小时、分钟和秒数
    const secondsInAHour = 60 * 60 * 1000;
    const secondsInAMinute = 60 * 1000;

    const hours = Math.floor(timeDifference / secondsInAHour);
    const mins = Math.floor((timeDifference % secondsInAHour) / secondsInAMinute);
    const secs = Math.floor((timeDifference % secondsInAMinute) / 1000);

    // 判断是否为简洁模式
    if (this.dataset.compact == 'true') {
      const otherHTML = `<div class="countdown__item"><span>${hours > 9 ? hours : '0' + hours}:${mins > 9 ? mins : '0' + mins}:${secs > 9 ? secs : '0' + secs}</span></div>`;
      this.innerHTML = otherHTML;
    } else {
      const hourHTML = `<div class="countdown__item"><span class="time">${hours > 9 ? hours : '0' + hours}</span><span class="colon"> : </span></div>`;
      const minHTML = `<div class="countdown__item"><span class="time">${mins > 9 ? mins : '0' + mins}</span><span class="colon"> : </span></div>`;
      const secHTML = `<div class="countdown__item"><span class="time">${secs > 9 ? secs : '0' + secs}</span></div>`;

      this.innerHTML = hourHTML + minHTML + secHTML;
    }
  }

  // 重置计时器
  resetTimer() {
    // 重置计时器为新的24小时周期
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // 获取今天零点时间
    this.date = startOfDay.getTime() + this.duration; // 计算新的目标时间为当前时间+24小时

    // 启动新一轮的倒计时
    this.startTimer();
  }

  unload() {
    if (this.dataset.interval) {
      clearInterval(this.dataset.interval);
    }

    this.classList.add('hidden');
    if (this.parent) {
      this.parent.classList.add('hidden');
    }
  }
}

customElements.define('loop-countdown-timer', LoopCountdownTimer);

/*
class CustomPayLater extends HTMLElement {
  constructor() {
    super();
    this.init();
  }
  init() {
    this.klarna = this.querySelector('.klarna-msg');
    this.afterpay = this.querySelector('.afterpay-msg');    
    const klarnaHtml = '<div class="modal-content" style="background-color: #fff; padding: 4rem;max-width: 600px;">'+
        '<div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px;"><span><div class="country-US" style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px;"><div style="box-sizing: border-box; display: flex; align-items: center; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; height: 60px; justify-content: center; opacity: 1;"><svg focusable="false" width="81" height="20" aria-hidden="true"><g transform="translate(0, 0) scale(1)"><path d="M78.3352549,14.3292706 C77.0678017,14.3292706 76.0403439,15.3567284 76.0403439,16.6243597 C76.0403439,17.8916348 77.0678017,18.9192707 78.3352549,18.9192707 C79.6027081,18.9192707 80.630344,17.8916348 80.630344,16.6243597 C80.630344,15.3567284 79.6027081,14.3292706 78.3352549,14.3292706" fill="#000"></path><path d="M70.7958568,7.22817345 L70.7958568,6.4467803 L74.4529833,6.4467803 L74.4529833,18.6618356 L70.7958568,18.6618356 L70.7958568,17.8811547 C69.7626656,18.5857975 68.5156063,19 67.1704277,19 C63.6107082,19 60.7250027,16.1142945 60.7250027,12.554575 C60.7250027,8.99485561 63.6107082,6.10915009 67.1704277,6.10915009 C68.5156063,6.10915009 69.7626656,6.52335256 70.7958568,7.22817345 Z M67.4697718,15.6974209 C69.3000267,15.6974209 70.7835696,14.2902722 70.7835696,12.554575 C70.7835696,10.8188779 69.3000267,9.41208536 67.4697718,9.41208536 C65.6395168,9.41208536 64.1559739,10.8188779 64.1559739,12.554575 C64.1559739,14.2902722 65.6395168,15.6974209 67.4697718,15.6974209 Z" fill="#000"></path><path d="M54.2263333,6.11823191 C52.765406,6.11823191 51.3828316,6.57178896 50.4584442,7.82312205 L50.4584442,6.4474926 L46.8169884,6.4474926 L46.8169884,18.6618356 L50.503141,18.6618356 L50.503141,12.2427657 C50.503141,10.3852653 51.7487757,9.47565814 53.2485235,9.47565814 C54.8558285,9.47565814 55.7798597,10.4358386 55.7798597,12.2174791 L55.7798597,18.6618356 L59.4327124,18.6618356 L59.4327124,10.8940256 C59.4327124,8.05141421 57.1725844,6.11823191 54.2263333,6.11823191" fill="#000"></path><path d="M41.5278044,8.03788051 L41.5278044,6.44695838 L37.7834212,6.44695838 L37.7834212,18.6618356 L41.536174,18.6618356 L41.536174,12.9588053 C41.536174,11.0347048 43.6216104,10.0004452 45.0686479,10.0004452 C45.0834281,10.0004452 45.097318,10.0018698 45.1120982,10.0020479 L45.1120982,6.44767068 C43.6269526,6.44767068 42.2609392,7.08357654 41.5278044,8.03788051" fill="#000"></path><path d="M32.2128788,7.22817345 L32.2128788,6.4467803 L35.8701833,6.4467803 L35.8701833,18.6618356 L32.2128788,18.6618356 L32.2128788,17.8811547 C31.1796876,18.5857975 29.9326283,19 28.5876277,19 C25.0279083,19 22.1422028,16.1142945 22.1422028,12.554575 C22.1422028,8.99485561 25.0279083,6.10915009 28.5876277,6.10915009 C29.9326283,6.10915009 31.1796876,6.52335256 32.2128788,7.22817345 Z M28.8867938,15.6974209 C30.7170487,15.6974209 32.2007697,14.2902722 32.2007697,12.554575 C32.2007697,10.8188779 30.7170487,9.41208536 28.8867938,9.41208536 C27.0567169,9.41208536 25.5729959,10.8188779 25.5729959,12.554575 C25.5729959,14.2902722 27.0567169,15.6974209 28.8867938,15.6974209 Z" fill="#000"></path><path d="M16.8150889 18.6618356 20.6429893 18.6618356 20.6429893 1.00338343 16.8150889 1.00338343z" fill="#000"></path><path d="M14.1770857,1 L10.2104649,1 C10.2104649,4.25111544 8.71570325,7.23511837 6.10957549,9.1873547 L4.53806353,10.3642524 L10.6271604,18.6673559 L15.6335612,18.6673559 L10.0307872,11.0272257 C12.6865979,8.38263373 14.1770857,4.82469505 14.1770857,1" fill="#000"></path><path d="M0 18.6666436 4.05334336 18.6666436 4.05334336 1 0 1z" fill="#000"></path></g></svg></div><h1 id="learn-more-title" role="heading" aria-level="1" style="max-width: 100%; margin: -5px 0px -10px; padding: 1px 0px 4px; box-sizing: border-box; flex-shrink: 0; text-rendering: geometricprecision; -webkit-font-smoothing: antialiased; word-break: break-word; text-align: left; direction: ltr; font-family: -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, Arial, sans-serif; font-size: 20px; font-weight: 700; line-height: 30px; letter-spacing: 0.3px; color: rgb(23, 23, 23); text-size-adjust: none;">Buy what you love and split the cost. It’s easy and interest-free.</h1><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; background-color: rgba(255, 255, 255, 0); width: 100%; height: 15px;"></div><div class="timeline"><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; background-color: rgba(255, 255, 255, 0); width: 100%; height: 15px;"></div><div role="list" style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px;"><div role="listitem" style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: row; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px;"><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: row; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px;"><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: absolute; z-index: 0; min-height: 0px; min-width: 0px; width: 2px; background-color: rgb(240, 238, 235); height: 100%; top: 15px; left: 4px;"></div><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; padding-top: 15px; padding-right: 15px;"><svg focusable="false" width="10" height="10"><rect width="10" height="10" fill="rgba(23, 23, 23, 1)"></rect></svg></div></div><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 1; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; width: 100%; padding-top: 15px;"><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; padding-bottom: 15px;"><p style="max-width: 100%; padding-bottom: 4px; padding-top: 1px; font-family: -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, Arial, sans-serif; font-weight: 500; font-size: 16px; line-height: 20px; color: rgb(23, 23, 23); text-rendering: geometricprecision; -webkit-font-smoothing: antialiased; white-space: pre-line; margin-bottom: -10px; margin-top: -5px; letter-spacing: 0px; text-size-adjust: none;">Add item(s) to your cart</p></div><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; padding-bottom: 15px;"><p style="max-width: 100%; color: rgb(120, 117, 115); font-size: 14px; font-family: -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, Arial, sans-serif; font-weight: 400; line-height: 20px; text-rendering: geometricprecision; -webkit-font-smoothing: antialiased; text-align: left; word-break: break-word; margin-bottom: -10px; margin-top: -5px; padding-bottom: 4px; padding-top: 1px; letter-spacing: 0px; text-size-adjust: none;"></p></div></div></div><div role="listitem" style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: row; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px;"><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: row; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px;"><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: absolute; z-index: 0; min-height: 0px; min-width: 0px; width: 2px; background-color: rgb(240, 238, 235); height: 100%; top: 0px; left: 4px;"></div><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; padding-top: 15px; padding-right: 15px;"><svg focusable="false" width="10" height="10"><rect width="10" height="10" fill="rgba(23, 23, 23, 1)"></rect></svg></div></div><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 1; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; width: 100%; padding-top: 15px;"><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; padding-bottom: 15px;"><p style="max-width: 100%; padding-bottom: 4px; padding-top: 1px; font-family: -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, Arial, sans-serif; font-weight: 500; font-size: 16px; line-height: 20px; color: rgb(23, 23, 23); text-rendering: geometricprecision; -webkit-font-smoothing: antialiased; white-space: pre-line; margin-bottom: -10px; margin-top: -5px; letter-spacing: 0px; text-size-adjust: none;">Go to checkout and choose <svg role="img" style="margin: -5px 5px; height: 30px; vertical-align: middle" focusable="false" viewBox="0 0 38 22" version="1.1" xmlns="http://www.w3.org/2000/svg"><title>Klarna badge</title><path d="M34.9997,22.0004 L2.9997,22.0004 C1.3437,22.0004 -0.0003,20.6564 -0.0003,19.0004 L-0.0003,3.0004 C-0.0003,1.3434 1.3437,0.0004 2.9997,0.0004 L34.9997,0.0004 C36.6567,0.0004 37.9997,1.3434 37.9997,3.0004 L37.9997,19.0004 C37.9997,20.6564 36.6567,22.0004 34.9997,22.0004" fill="rgba(255, 179, 199, 1)"></path><path d="M30.5661,13.3275 C29.8181,13.3275 29.2121,12.7535 29.2121,12.0435 C29.2121,11.3345 29.8181,10.7605 30.5661,10.7605 C31.3141,10.7605 31.9201,11.3345 31.9201,12.0435 C31.9201,12.7535 31.3141,13.3275 30.5661,13.3275 L30.5661,13.3275 Z M31.9241,9.5485 L31.9241,9.8675 C31.5031,9.5795 30.9931,9.4105 30.4441,9.4105 C28.9891,9.4105 27.8101,10.5895 27.8101,12.0435 C27.8101,13.4985 28.9891,14.6775 30.4441,14.6775 C30.9931,14.6775 31.5031,14.5085 31.9241,14.2205 L31.9241,14.5395 L33.4201,14.5395 L33.4201,9.5485 L31.9241,9.5485 Z" fill="rgba(23, 23, 23, 1)"></path><path d="M8.7917,7.3226 L7.1707,7.3226 C7.1707,8.6506 6.5607,9.8706 5.4947,10.6686 L4.8537,11.1486 L7.3407,14.5416 L9.3867,14.5416 L7.0977,11.4196 C8.1827,10.3396 8.7917,8.8846 8.7917,7.3226" fill="rgba(23, 23, 23, 1)"></path><path d="M2.995,14.541 L4.655,14.541 L4.655,7.322 L2.995,7.322" fill="rgba(23, 23, 23, 1)"></path><path d="M9.869,14.539 L11.433,14.539 L11.433,7.324 L9.869,7.324" fill="rgba(23, 23, 23, 1)"></path><path d="M25.1549,9.4144 C24.5579,9.4144 23.9929,9.5994 23.6149,10.1104 L23.6149,9.5484 L22.1279,9.5484 L22.1279,14.5394 L23.6339,14.5394 L23.6339,11.9164 C23.6339,11.1574 24.1419,10.7854 24.7559,10.7854 C25.4119,10.7854 25.7899,11.1784 25.7899,11.9054 L25.7899,14.5394 L27.2819,14.5394 L27.2819,11.3654 C27.2819,10.2034 26.3589,9.4144 25.1549,9.4144" fill="rgba(23, 23, 23, 1)"></path><path d="M14.8014,13.3275 C14.0534,13.3275 13.4474,12.7535 13.4474,12.0435 C13.4474,11.3345 14.0534,10.7605 14.8014,10.7605 C15.5494,10.7605 16.1554,11.3345 16.1554,12.0435 C16.1554,12.7535 15.5494,13.3275 14.8014,13.3275 L14.8014,13.3275 Z M16.1604,9.5485 L16.1604,9.8675 C15.7384,9.5795 15.2294,9.4105 14.6794,9.4105 C13.2254,9.4105 12.0454,10.5895 12.0454,12.0435 C12.0454,13.4985 13.2254,14.6775 14.6794,14.6775 C15.2294,14.6775 15.7384,14.5085 16.1604,14.2205 L16.1604,14.5395 L17.6554,14.5395 L17.6554,9.5485 L16.1604,9.5485 Z" fill="rgba(23, 23, 23, 1)"></path><path d="M19.9665,10.1986 L19.9665,9.5486 L18.4365,9.5486 L18.4365,14.5396 L19.9695,14.5396 L19.9695,12.2096 C19.9695,11.4236 20.8215,11.0006 21.4125,11.0006 L21.4315,11.0006 L21.4315,9.5486 C20.8235,9.5486 20.2665,9.8076 19.9665,10.1986" fill="rgba(23, 23, 23, 1)"></path><path d="M35.0055,12.7689 C34.4875,12.7689 34.0685,13.1889 34.0685,13.7059 C34.0685,14.2249 34.4875,14.6449 35.0055,14.6449 C35.5235,14.6449 35.9425,14.2249 35.9425,13.7059 C35.9425,13.1889 35.5235,12.7689 35.0055,12.7689" fill="rgba(23, 23, 23, 1)"></path></svg></p></div><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; padding-bottom: 15px;"><p style="max-width: 100%; color: rgb(120, 117, 115); font-size: 14px; font-family: -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, Arial, sans-serif; font-weight: 400; line-height: 20px; text-rendering: geometricprecision; -webkit-font-smoothing: antialiased; text-align: left; word-break: break-word; margin-bottom: -10px; margin-top: -5px; padding-bottom: 4px; padding-top: 1px; letter-spacing: 0px; text-size-adjust: none;"></p></div></div></div><div role="listitem" style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: row; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px;"><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: row; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px;"><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: absolute; z-index: 0; min-height: 0px; min-width: 0px; width: 2px; background-color: rgb(240, 238, 235); height: 100%; top: 0px; left: 4px;"></div><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; padding-top: 15px; padding-right: 15px;"><svg focusable="false" width="10" height="10"><rect width="10" height="10" fill="rgba(23, 23, 23, 1)"></rect></svg></div></div><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 1; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; width: 100%; padding-top: 15px;"><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; padding-bottom: 15px;"><p style="max-width: 100%; padding-bottom: 4px; padding-top: 1px; font-family: -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, Arial, sans-serif; font-weight: 500; font-size: 16px; line-height: 20px; color: rgb(23, 23, 23); text-rendering: geometricprecision; -webkit-font-smoothing: antialiased; white-space: pre-line; margin-bottom: -10px; margin-top: -5px; letter-spacing: 0px; text-size-adjust: none;">Enter your debit or credit card information</p></div><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; padding-bottom: 15px;"><p style="max-width: 100%; color: rgb(120, 117, 115); font-size: 14px; font-family: -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, Arial, sans-serif; font-weight: 400; line-height: 20px; text-rendering: geometricprecision; -webkit-font-smoothing: antialiased; text-align: left; word-break: break-word; margin-bottom: -10px; margin-top: -5px; padding-bottom: 4px; padding-top: 1px; letter-spacing: 0px; text-size-adjust: none;"></p></div></div></div><div role="listitem" style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: row; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px;"><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: row; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px;"><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: absolute; z-index: 0; min-height: 0px; min-width: 0px; width: 2px; background-color: rgb(240, 238, 235); height: 15px; top: 0px; left: 4px;"></div><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; padding-top: 15px; padding-right: 15px;"><svg focusable="false" width="10" height="10"><rect width="10" height="10" fill="rgba(23, 23, 23, 1)"></rect></svg></div></div><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 1; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; width: 100%; padding-top: 15px;"><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; padding-bottom: 15px;"><p style="max-width: 100%; padding-bottom: 4px; padding-top: 1px; font-family: -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, Arial, sans-serif; font-weight: 500; font-size: 16px; line-height: 20px; color: rgb(23, 23, 23); text-rendering: geometricprecision; -webkit-font-smoothing: antialiased; white-space: pre-line; margin-bottom: -10px; margin-top: -5px; letter-spacing: 0px; text-size-adjust: none;">Your first payment is taken when the order is processed and the remaining 3 are automatically taken every two weeks</p></div><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; padding-bottom: 15px;"><p style="max-width: 100%; color: rgb(120, 117, 115); font-size: 14px; font-family: -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, Arial, sans-serif; font-weight: 400; line-height: 20px; text-rendering: geometricprecision; -webkit-font-smoothing: antialiased; text-align: left; word-break: break-word; margin-bottom: -10px; margin-top: -5px; padding-bottom: 4px; padding-top: 1px; letter-spacing: 0px; text-size-adjust: none;"></p></div></div></div></div></div><div style="box-sizing: border-box; display: flex; align-items: stretch; flex-direction: column; flex-shrink: 0; border-style: solid; border-width: 0px; position: relative; z-index: 0; min-height: 0px; min-width: 0px; background-color: rgba(255, 255, 255, 0); width: 100%; height: 15px;"></div><p style="max-width: 100%; color: rgb(120, 117, 115); font-family: -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, Arial, sans-serif; font-weight: 400; font-size: 12px; line-height: 20px; text-rendering: geometricprecision; -webkit-font-smoothing: antialiased; text-align: left; word-break: break-word; margin-bottom: -10px; margin-top: -5px; padding-bottom: 4px; padding-top: 1px; letter-spacing: 0px; text-size-adjust: none;">See payment <a tabindex="0" aria-label="Klarna payment terms (Opens in a new window)" href="https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_us/sliceitinx" target="_blank" style="border-radius: 2px; color: rgb(120, 117, 115); cursor: pointer; font-family: -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, Arial, sans-serif; font-weight: 400; outline: 0px; text-decoration: underline rgb(120, 117, 115); text-rendering: geometricprecision; transition: color 0.2s ease 0s; -webkit-font-smoothing: antialiased;">terms</a>. A higher initial payment may be required for some consumers. CA residents: Loans made or arranged pursuant to a California Financing Law license.</p></div></span></div>'+
        '<div class="modal-close"><svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 64 64"><defs><style>.cls-1{fill:none;stroke:#000;stroke-miterlimit:10;stroke-width:2px}</style></defs><path class="cls-1" d="M19 17.61l27.12 27.13m0-27.13L19 44.74"/></svg></div>'+
      '</div>';
    this.klarna && this.klarna.addEventListener('click', (e)=> {
      e.preventDefault();
      let klarnaModal = document.querySelector('#klarna-modal');
      if (!klarnaModal) {
        klarnaModal = document.createElement('div');
        klarnaModal.id = 'klarna-modal';
        klarnaModal.classList.add('modal-box');
        klarnaModal.innerHTML = klarnaHtml;
        document.body.appendChild(klarnaModal);        
      }
      klarnaModal.addEventListener('click', (e)=> { e.stopPropagation(); e.target == klarnaModal && document.body.removeChild(klarnaModal) });
      klarnaModal.querySelector('.modal-close').addEventListener('click', (e)=> { e.stopPropagation(); document.body.removeChild(klarnaModal) });      
    });
    
    this.afterpay && this.afterpay.addEventListener('click', (e)=> {
      e.preventDefault();
      Afterpay.version && Afterpay.launchModal();
    });
  }
}
customElements.define('custom-pay-later', CustomPayLater);
*/

class CarouselComponent extends HTMLElement {
  constructor() {
    super();

    if (this.dataset.slider == 'true') {
      this.settings = {
        autorotate: this.dataset.autorotate == 'true',
        autorotateSpeed: parseInt(this.dataset.autorotateSpeed),
        showDots: this.dataset.showDots == 'true',
        showArrows: this.dataset.showArrows == 'true'
      };
      this.slider = this.querySelector('.carousel__list');
      this.prev = this.querySelectorAll('.slider-button--prev');
      this.next = this.querySelectorAll('.slider-button--next');
      theme.initWhenVisible({
        element: this,
        callback: this.init.bind(this),
        threshold: 10
      });
      theme.initWhenVisible({
        element: this,
        callback: this.update.bind(this),
        threshold: 0
      });
      
    }
  }

  init() {
    setTimeout(() => {
      if (this.slider) {
        this.flickity = new Flickity(this.slider, {
          accessibility: false,
          rightToLeft: theme.config.rtl,
          prevNextButtons: this.settings.showArrows,
          arrowShape: 'M29.043 19.756l-28.125 28.125c-0.605 0.606-0.918 1.406-0.918 2.031s0.305 1.599 0.916 2.209l28.125 28.125c1.221 1.221 3.199 1.221 4.418 0s1.221-3.199 0-4.418l-22.793-22.793h86.209c1.727 0 3.125-1.398 3.125-2.949 0-1.727-1.398-3.125-3.125-3.125h-86.211l22.793-22.793c1.221-1.221 1.221-3.199 0-4.418s-3.203-1.217-4.414 0.006z',
          pageDots: this.settings.showDots,
          wrapAround: false,
          draggable: true,
          cellAlign: 'left',
          contain: true,
          autoPlay: this.settings.autorotate ? this.settings.autorotateSpeed : false,
          pauseAutoPlayOnHover: true
        });
      }
  
      
      if (this.settings.autorotate) {
        this.flickity.pausePlayer();
      }
  
    });
  }

  update() {
    setTimeout(() => {

      this.prev.forEach((prev) => {
        prev.addEventListener('click', (e) => {
          e.preventDefault();
          this.flickity.previous();
        });
      });
      this.next.forEach((next) => {
        next.addEventListener('click', (e) => {
          e.preventDefault();
          this.flickity.next();
        });
      }); 
     
        
        if (this.settings.autorotate) {
          this.flickity.unpausePlayer();
        }


      
    }, 300);
  }

  getSwipeDirection(moveVector) {
    return moveVector.x > 0 ? 'right' : 'left';
  }
}
customElements.define('carousel-component', CarouselComponent);

if (!customElements.get("loop-countdown")) {
  class LoopCountdown extends HTMLElement {
    constructor() {
      super();
      this.timerInterval = null;
      this.endTime = null;
      this._initialized = false; 
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      // ✅ 如果有 theme 懒加载就用，没有就直接 init
      if (window.theme && theme.initWhenVisible) {
        theme.initWhenVisible({
          element: this,
          callback: () => this.initCountdown(),
          threshold: 200
        });
      } else {
        this.initCountdown();
      }
    }
     disconnectedCallback() {
      // ✅ 清理定时器（非常关键）
      this.cleanup();
      this._initialized = false;
    }

    cleanup() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    }

    initCountdown() {
      try {
        this.parseAttributes();
        this.calculateEndTime();
        this.updateTimer();
        this.cleanup();
        // 使用 setInterval 每秒触发，但精度由真实时间控制
        this.timerInterval = setInterval(() => this.updateTimer(), 1000);
      } catch (error) {
        console.error('Countdown initialization failed:', error);
        this.style.display = 'none';
        clearInterval(this.timerInterval);
      }
    }

    parseAttributes() {
      this.timezone = this.getAttribute("data-time-zone") || "local";
      this.endDate = this.getAttribute("data-end-date");
      if (this.timezone !== "local" && !/^[-+]?\d+$/.test(this.timezone)) {
        throw new Error('Invalid timezone format');
      }
    }

    calculateEndTime() {
      const now = new Date();
      if (this.endDate) {
        this.endTime = this.parseEndDate();
        if (this.endTime <= now) {
          this.style.display = 'none';
          throw new Error('End date must be in the future');
        }
      } else {
        this.endTime = this.calculateNextMidnight();
      }
    }

    parseEndDate() {
      if (this.timezone === "local") {
        const date = new Date(this.endDate);
        if (isNaN(date)) throw new Error('Invalid end date format');
        return date;
      }
      const offset = parseInt(this.timezone, 10);
      return this.convertToUTC(this.endDate, offset);
    }

    convertToUTC(dateString, offset) {
      const parts = dateString.split(/[-T:]/).map(Number);
      if (parts.length < 6) throw new Error('Invalid date format');
      const [year, month, day, hour, minute, second] = parts;
      return new Date(Date.UTC(
        year,
        month - 1,
        day,
        hour - offset,
        minute,
        second
      ));
    }

    calculateNextMidnight() {
      const now = new Date();
      let offset = this.timezone === "local" 
        ? -now.getTimezoneOffset() / 60 
        : parseInt(this.timezone, 10);

      const targetTime = now.getTime() + offset * 3600 * 1000;
      const targetDate = new Date(targetTime);

      const midnightUTC = Date.UTC(
        targetDate.getUTCFullYear(),
        targetDate.getUTCMonth(),
        targetDate.getUTCDate() + 1,
        0, 0, 0
      ) - offset * 3600 * 1000;

      return new Date(midnightUTC);
    }

    updateTimer() {
      const now = new Date();
      let diff = this.endTime - now;

      if (diff <= 0) {
        if (!this.endDate) {
          // 循环模式，重新计算下一个循环
          this.calculateEndTime();
          diff = this.endTime - now;
        } else {
          this.style.display = 'none';
          clearInterval(this.timerInterval);
          this.timerInterval = null;
          return;
        }
      }

      const { days, hours, minutes, seconds } = this.calculateTimeParts(diff);
      this.updateDisplay(days, hours, minutes, seconds);
    }

    calculateTimeParts(diff) {
      if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      const secondsTotal = Math.floor(diff / 1000);
      const days = Math.floor(secondsTotal / 86400);
      let remaining = secondsTotal % 86400;
      const hours = Math.floor(remaining / 3600);
      remaining %= 3600;
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      return { days, hours, minutes, seconds };
    }

    updateDisplay(days, hours, minutes, seconds) {
      if (days > 0) {
        this.updateUnit('.days', days);
        this.toggleVisibility('.days', true);
        this.toggleSeparatorVisibility(0, true);
      } else {
        this.toggleVisibility('.days', false);
        this.toggleSeparatorVisibility(0, false);
      }
      this.updateUnit('.hours', hours);
      this.updateUnit('.minutes', minutes);
      this.updateUnit('.seconds', seconds);
    }

    toggleVisibility(selector, visible) {
      const el = this.querySelector(selector);
      if (el) el.style.display = visible ? 'flex' : 'none';
    }

    toggleSeparatorVisibility(index, visible) {
      const separators = this.querySelectorAll('.separator');
      if (separators[index]) separators[index].style.display = visible ? 'inline' : 'none';
    }

    updateUnit(selector, value) {
      const group = this.querySelector(selector);
      if (!group) return;
      const digits = group.querySelectorAll('.digit');
      const strValue = value.toString().padStart(2, '0');
      if (digits[0] && digits[0].textContent !== strValue[0]) {
        digits[0].textContent = strValue[0];
        this.animateDigit(digits[0]);
      }
      if (digits[1] && digits[1].textContent !== strValue[1]) {
        digits[1].textContent = strValue[1];
        this.animateDigit(digits[1]);
      }
    }

    animateDigit(el) {
      el.classList.add('flip');
      setTimeout(() => el.classList.remove('flip'), 100);
    }
  }

  customElements.define("loop-countdown", LoopCountdown);
}



/*
*无法点击的选项。在切换变体的时候，会自动将其顺延到其他可选的选项
*/
// 查找下一个有效的 radio 按钮
function findNextRadio(radio) {
  let next = radio.nextElementSibling;
  while (next && (next.disabled || next.type !== 'radio')) {
    next = next.nextElementSibling;
  }
  return next;
}

// 查找前一个有效的 radio 按钮
function findPrevRadio(radio) {
  let prev = radio.previousElementSibling;
  while (prev && (prev.disabled || prev.type !== 'radio')) {
    prev = prev.previousElementSibling;
  }
  return prev;
}

subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {

  const radios = document.querySelectorAll("input.button-input__input[type='radio']");

  for (const radio of radios) {
    if (radio.checked && radio.disabled) {
      // 优先尝试下一个可用的 radio
      const nextRadio = findNextRadio(radio);
      if (nextRadio) {
        nextRadio.click();
      } else {
        // 如果没有下一个，再尝试前一个
        const prevRadio = findPrevRadio(radio);
        if (prevRadio) {
          prevRadio.click();
        } else {
          console.log('没有找到可用的 radio');
        }
      }
      break; // 找到并处理一个就结束
    }
  }
});

//自定义omnisend弹框
function openOmnisendPop(){
    window.omnisend = window.omnisend || [];
    window.omnisend.push(['openForm', '681b0abc6a35d55a0035030d']);
}

//聊天框样式
/*
  let scrollTimer = null;
  window.addEventListener('load', () => {
    const chatElem = document.querySelector('#ShopifyChat');

    if (!chatElem) return;

    chatElem.style.transform = 'translateX(67%)';


    setTimeout(() => showChat(chatElem), 4000);


    window.addEventListener('scroll', () => {
      hideChat(chatElem);

      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
      scrollTimer = setTimeout(() => {
        showChat(chatElem);
      }, 4000);
    });
    chatElem.addEventListener('click', ()=> {
      showChat(chatElem);
    });
  });
  function showChat(elem) {
    if (elem) elem.style.transform = 'translateX(0)';
  }
  function hideChat(elem) {
    if (elem) elem.style.transform = 'translateX(67%)';
  }
*/
/* 目前应用与account页面的切换 */
if (!customElements.get('tab-switcher')) {
  class TabSwitcher extends HTMLElement {
    constructor() {
      super();
      this.onTabClick = this.onTabClick.bind(this);
      this.initialized = false;
    }

    connectedCallback() {
      theme.initWhenVisible({
        element: this,
        threshold: 100,
        callback: () => this.init()
      });
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;

      this.tabs = this.querySelectorAll('.tab');
      this.panels = this.querySelectorAll('.panel');

      // 自动分配 index
      this.tabs.forEach((tab, i) => {
        tab.dataset.index = i;
        tab.addEventListener('click', this.onTabClick);
      });

      this.panels.forEach((panel, i) => {
        panel.dataset.index = i;
      });
    }

    onTabClick(e) {
      const index = e.currentTarget.dataset.index;
      this.switchTab(index);
    }

    switchTab(index) {
      this.tabs.forEach(tab => tab.classList.remove('active'));
      this.panels.forEach(panel => panel.classList.remove('active'));

      if (this.tabs[index]) this.tabs[index].classList.add('active');
      if (this.panels[index]) this.panels[index].classList.add('active');
    }

    disconnectedCallback() {
      if (!this.tabs) return;
      this.tabs.forEach(tab => {
        tab.removeEventListener('click', this.onTabClick);
      });
    }
  }

  customElements.define('tab-switcher', TabSwitcher);
}



if (!customElements.get('copy-content')) {
  class CopyContent extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', () => this.copyToClipboard());
    }

    connectedCallback() {
      this.dataCopyText = this.dataset.copyText || this.textContent.trim();
      this.url = this.dataset.url;
      this.autoRedirect = this.dataset.autoRedirect === 'true';
      this.delay = parseInt(this.dataset.delay || 1500, 10);
      this.messageId = this.dataset.messageId;
       this.scrollTarget = this.dataset.scroll;
    }

    async copyToClipboard() {
      if (!this.dataCopyText) return;
      try {
        await navigator.clipboard.writeText(this.dataCopyText);
        this.showCopyMessage();
        setTimeout(() => {
          this.handleScroll();
        },2000);
        this.delayedRedirect();
      } catch (err) {
        console.error('Copy failed：', err);
      }
    }

    showCopyMessage() {
      if (!this.messageId) return;
      const message = document.getElementById(this.messageId);
      if (!message) return;
      message.style.display = 'inline-flex';
      setTimeout(() => (message.style.display = 'none'), this.delay);
    }

    handleScroll() {
      if (!this.scrollTarget) return;

      const openedModal = document.querySelector('modal-dialog[open]');

      if (openedModal && typeof openedModal.hide === 'function') {
        openedModal.hide();
      }

      const targetEl = document.getElementById(this.scrollTarget);
      if (!targetEl) return;

      const header = document.querySelector('.header-wrapper');
      const offset = header ? header.offsetHeight : 0;

      setTimeout(() => {
        const rect = targetEl.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        const top = rect.top + scrollTop - offset;

        window.scrollTo({
          top,
          behavior: 'smooth'
        });
      }, 100);
    }

    delayedRedirect() {
      if (this.url && this.autoRedirect) {
        setTimeout(() => {
          window.location.href = this.url;
        }, this.delay);
      }
    }
  }
  customElements.define('copy-content', CopyContent);
}

class AnchorMenu extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.anchor-menu-item')];
    this.sections = this.items.map(item => {
      const id = item.getAttribute('href').replace('#', '');
      return document.getElementById(id);
    });

    this.menu = this; // 水平滚动容器
    this.activeId = null;

    this.onClick = this.onClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);

    // 点击菜单滚动
    this.items.forEach(item => item.addEventListener('click', this.onClick));

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);

    // 页面加载时校准 active
    setTimeout(() => this.updateActive(), 0);
  }

  disconnectedCallback() {
    this.items.forEach(item => item.removeEventListener('click', this.onClick));
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
  }

  getOffset() {
    const header = document.querySelector('.shopify-section-group-header-group.shopify-section-header');
    const headerHeight = header ? header.offsetHeight : 0;
    const menuHeight = this.offsetHeight || 0;
    return headerHeight + menuHeight + 40; // 可根据需要调整
  }

  onClick(e) {
    e.preventDefault();
    const item = e.currentTarget;
    const id = item.getAttribute('href').replace('#', '');
    const section = document.getElementById(id);
    if (!section) return;

    const top = section.offsetTop - this.getOffset();
    window.scrollTo({ top, behavior: 'smooth' });

    // 点击时立即反馈
    this.setActive(id);
    this.scrollItemIntoView(item);
  }

  onScroll() {
    // 直接更新 active
    this.updateActive();
  }

  onResize() {
    // 可能 header 高度变化
    this.updateActive();
  }

  setActive(id) {
    if (this.activeId === id) return;
    this.activeId = id;

    this.items.forEach(item => {
      const match = item.getAttribute('href') === `#${id}`;
      item.classList.toggle('active', match);
      if (match) this.scrollItemIntoView(item);
    });
  }

  updateActive() {
    const scrollTop = window.scrollY;
    const offset = this.getOffset();

    let activeId = null;

    for (let i = 0; i < this.sections.length; i++) {
      const current = this.sections[i];
      const next = this.sections[i + 1];

      if (!current) continue;

      const currentTop = current.offsetTop - offset;
      const nextTop = next ? next.offsetTop - offset : Infinity;

      if (scrollTop >= currentTop && scrollTop < nextTop) {
        activeId = current.id;
        break;
      }
    }

    if (activeId) {
      this.setActive(activeId);
    }
  }

scrollItemIntoView(item) {
  if (!this.menu) return;

  const menuRect = this.menu.getBoundingClientRect();
  const itemRect = item.getBoundingClientRect();

  const menuScrollLeft = this.menu.scrollLeft;
  const menuWidth = menuRect.width;
  const itemWidth = itemRect.width;

  const isFirst = this.items[0] === item;
  const isLast = this.items[this.items.length - 1] === item;

  if (isFirst) {
    // 第一个，左对齐
    this.menu.scrollBy({
      left: itemRect.left - menuRect.left - 8,
      behavior: 'smooth'
    });
  } else if (isLast) {
    // 最后一个，右对齐
    this.menu.scrollBy({
      left: itemRect.right - menuRect.right + 8,
      behavior: 'smooth'
    });
  } else {
    // 中间项，居中
    const targetScrollLeft = menuScrollLeft + (itemRect.left + itemWidth / 2) - (menuWidth / 2);
    this.menu.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    });
  }
}

}

customElements.define('anchor-menu', AnchorMenu);


/* 这个在购物车里面的赠送产品提示使用，张可心。根据进入的时间倒计时，超过就不显示 */
class PromoCountdown extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
    this.interval = null;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.init();
  }

  init() {
    const hours = Number(this.getAttribute('data-hours')) || 2;
    const key = this.getAttribute('data-key') || 'default';

    this.storageKey = `promo_countdown_${key}`;

    this.endTime = this.getEndTime(hours);

    this.cacheDOM();
    this.start();
  }

  getEndTime(hours) {
    const raw = localStorage.getItem(this.storageKey);
    const now = Date.now();

    // ✅ 新增：统一结构支持 createdAt
    if (raw) {
      try {
        const data = JSON.parse(raw);

        const isValid =
          data &&
          typeof data.endTime === 'number' &&
          typeof data.createdAt === 'number';

        // ✅ 1天后清理（你可以改成 2天/7天）
        const isExpiredByTime =
          now - data.createdAt > 24 * 60 * 60 * 1000;

        if (isValid && !isExpiredByTime) {
          return data.endTime;
        }

        // ❌ 过期才清理
        localStorage.removeItem(this.storageKey);
      } catch (e) {
        localStorage.removeItem(this.storageKey);
      }
    }

    // 👉 创建新的倒计时
    const endTime = now + hours * 60 * 60 * 1000;

    localStorage.setItem(
      this.storageKey,
      JSON.stringify({
        endTime,
        createdAt: now
      })
    );

    return endTime;
  }

  cacheDOM() {
    this.hTens = this.querySelector('.h-tens');
    this.hOnes = this.querySelector('.h-ones');

    this.mTens = this.querySelector('.m-tens');
    this.mOnes = this.querySelector('.m-ones');

    this.sTens = this.querySelector('.s-tens');
    this.sOnes = this.querySelector('.s-ones');
  }

  start() {
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    if (this.interval) clearInterval(this.interval);
  }

  split(num) {
    return String(num).padStart(2, '0').split('');
  }

  setDigits(tensEl, onesEl, value) {
    const [t, o] = this.split(value);
    if (tensEl.innerText !== t) tensEl.innerText = t;
    if (onesEl.innerText !== o) onesEl.innerText = o;
  }

  tick() {
    const diff = this.endTime - Date.now();

    if (diff <= 0) {
      this.renderZero();
      this.finish();
      return;
    }

    const total = Math.floor(diff / 1000);

    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;

    this.setDigits(this.hTens, this.hOnes, h);
    this.setDigits(this.mTens, this.mOnes, m);
    this.setDigits(this.sTens, this.sOnes, s);
  }

  renderZero() {
    this.setDigits(this.hTens, this.hOnes, 0);
    this.setDigits(this.mTens, this.mOnes, 0);
    this.setDigits(this.sTens, this.sOnes, 0);
  }

  finish() {
    clearInterval(this.interval);
    this.classList.add('expired');

    const wrapper = this.closest('.promo-countdown-block');
    if (wrapper) {
      wrapper.classList.add('countdown-expired');
    }

    this.dispatchEvent(new CustomEvent('promo:end', {
      bubbles: true
    }));
  }
}

customElements.define('promo-countdown', PromoCountdown);

/* 固定时间循环倒计时，使用按钮 */
class FixedCountdown extends HTMLElement {
  constructor() {
    super();

    this._initialized = false;
    this.interval = null;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.hours = Number(this.dataset.hours) || 2;
    this.key = this.dataset.key;

    if (!this.key) return;

    this.storageKey = `fixed_countdown_${this.key}`;

    this.cacheDOM();

    const startTime = localStorage.getItem(this.storageKey);

    if (startTime) {
      this.endTime = this.getEndTime();
      this.start();
    }
  }

  disconnectedCallback() {
    clearInterval(this.interval);
    this.interval = null;
  }

  /* ---------------- DOM ---------------- */
  cacheDOM() {
    this.hTens = this.querySelector('.h-tens');
    this.hOnes = this.querySelector('.h-ones');

    this.mTens = this.querySelector('.m-tens');
    this.mOnes = this.querySelector('.m-ones');

    this.sTens = this.querySelector('.s-tens');
    this.sOnes = this.querySelector('.s-ones');
  }

  /* ---------------- 外部调用：开始 ---------------- */
  startCountdown() {
    // 已启动：不重置，只恢复
    if (localStorage.getItem(this.storageKey)) {
      this.endTime = this.getEndTime();
      this.start();
      return false;
    }

    const now = Date.now();

    localStorage.setItem(
      this.storageKey,
      String(now)
    );

    this.endTime = this.getEndTime();

    this.start();

    return true;
  }

  /* ---------------- 启动计时器 ---------------- */
  start() {
    this.tick();

    clearInterval(this.interval);

    this.interval = setInterval(() => {
      this.tick();
    }, 1000);
  }

  /* ---------------- 固定周期循环核心 ---------------- */
  getEndTime() {
    const startTime = Number(
      localStorage.getItem(this.storageKey)
    );

    if (!startTime) return null;

    const duration =
      this.hours * 60 * 60 * 1000;

    const elapsed = Date.now() - startTime;

    const cycle = Math.floor(elapsed / duration);

    return (
      startTime + (cycle + 1) * duration
    );
  }

  /* ---------------- tick ---------------- */
  tick() {
    let diff = this.endTime - Date.now();

    // 当前周期结束 → 自动进入下一周期
    if (diff <= 0) {
      this.endTime = this.getEndTime();
      diff = this.endTime - Date.now();
    }

    const total = Math.floor(diff / 1000);

    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;

    this.setDigits(this.hTens, this.hOnes, h);
    this.setDigits(this.mTens, this.mOnes, m);
    this.setDigits(this.sTens, this.sOnes, s);
  }

  /* ---------------- UI ---------------- */
  split(num) {
    return String(num).padStart(2, '0').split('');
  }

  setDigits(tensEl, onesEl, value) {
    const [t, o] = this.split(value);

    if (tensEl && tensEl.textContent !== t) {
      tensEl.textContent = t;
    }

    if (onesEl && onesEl.textContent !== o) {
      onesEl.textContent = o;
    }
  }
}

if (!customElements.get('fixed-countdown')) {
  customElements.define('fixed-countdown', FixedCountdown);
}


  /* view more button */
document.addEventListener('click', function (e) {
  const btn = e.target.closest('.view-more-btn');
  if (!btn) return;

  const group = btn.dataset.target;
  const targets = document.querySelectorAll(`[data-group="${group}"]`);

  if (!targets.length) return;

  const isHidden = btn.dataset.expanded !== 'true';

  btn.dataset.expanded = isHidden ? 'true' : 'false';

  targets.forEach(el => {
    el.classList.toggle('hidden', !isHidden);
  });

  btn.textContent = isHidden ? 'View less' : 'View more';
});

